/**
 * KizunaAI Quick Start Page
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app on Windows; the ones
 * not retaken for v0.44.0 are from v0.41.1.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  pageTitle: 'KizunaAI Quick Start',
  backLink: 'Back to AI Providers',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: 'Overview',
    content:
      'KizunaAI is the quickest way to start with Sokuji: there is no API key to create. You sign in with a Kizuna AI account, and the translation runs on one of two engines, Soniox or Qwen, which Kizuna AI pays for and bills to your balance. Accounts with a verified email address get a trial credit (currently US$0.50, valid for 7 days); after that you top up your balance and pay only for what you use, with no subscription. This guide walks through the first launch of the Sokuji desktop app. The screenshots are from Sokuji 0.44.0 on Windows; macOS and Linux look the same.',
  },
  steps: [
    {
      title: 'Install Sokuji',
      content:
        'Download the desktop app from the <a href="/download">download page</a> and install it (see the <a href="/docs/install/windows">Windows</a>, <a href="/docs/install/macos">macOS</a> and <a href="/docs/install/linux">Linux</a> guides). On first launch, Sokuji opens a six-step setup guide.',
      tip: 'On Windows, Sokuji asks to install VB-CABLE, a free virtual audio driver, when it starts. Click Install Now if you want the other side of a call to hear your translated voice.',
    },
    {
      title: 'Pick the interface language',
      content:
        'This is only the language of Sokuji’s menus and buttons. You choose the languages to translate between later. Click <strong>Next</strong>.',
      screenshot: '01-language.png',
    },
    {
      title: 'Tell Sokuji what you want to do',
      content:
        'Pick the card closest to how you will use Sokuji. It sets the translation mode (<strong>Me</strong>, <strong>Other</strong> or <strong>Both</strong>) and whether translations are spoken or shown as subtitles only. <strong>Understand what others say</strong> gives you a live translation of what you hear; <strong>Be understood in a meeting</strong> lets the other side hear your translated voice; <strong>Two-way online conversation</strong> does both at once, and once Sokuji listens to your meeting app it also reads the other side’s translation aloud to you. The two <strong>Face-to-face conversation</strong> cards are for two people at one computer. You can change all of this later.',
      screenshot: '02-scenario.png',
      tip: 'For a video call with someone who speaks another language, choose Two-way online conversation, then follow the two-way meeting guide. For someone sitting next to you, choose Face-to-face conversation and follow the face-to-face guide.',
    },
    {
      title: 'Choose “Start right away”',
      content:
        'Choose <strong>Start right away</strong>. Kizuna AI then runs the translation for you, so there is no API key to create or paste; the setup picks the Soniox engine, and you can switch to Qwen later (see below). Click <strong>Next</strong>.',
      screenshot: '03-provider.png',
    },
    {
      title: 'Sign in or create an account',
      content:
        'Click <strong>Create account</strong> if you are new to Kizuna AI, or <strong>Sign in</strong> if you already have an account.',
      screenshot: '04-account.png',
    },
    {
      title: 'Fill in the sign-up form',
      content:
        'Enter your name, your email address and a password of at least 8 characters, then click <strong>Sign Up</strong>. The same account also signs you in to the Kizuna AI website, where you manage your balance.',
      screenshot: '05-create-account.png',
    },
    {
      title: 'Verify your email address',
      content:
        'Sokuji signs you in straight away and emails you a verification link. Open that link: the trial credit is added to your balance once your address is verified. Then click <strong>Next</strong>.',
      screenshot: '06-verify-email.png',
      tip: 'No email? Check your spam folder. You can finish the setup in the meantime; the credit shows up on the account button as soon as you verify.',
    },
    {
      title: 'Choose your languages',
      content:
        '<strong>I speak</strong> is your language; <strong>they hear</strong> is the language the other side gets. In two-way mode the reverse direction is set for you, and the line underneath shows it: here, “They speak Japanese → I read English”. It says “I hear” instead when the other side’s translation will be read aloud to you.',
      screenshot: '07-languages.png',
      tip: 'If the other side’s speech is translated too (Other or Both mode), set I speak to a specific language rather than Auto Detect. Otherwise Sokuji cannot tell which language to translate them into, and Start stays disabled.',
    },
    {
      title: 'Review and finish',
      content:
        'Check the summary and click <strong>Finish</strong>. A short tour of the main window follows. You can skip it and replay it any time from <strong>Settings → Help</strong>.',
      screenshot: '08-ready.png',
    },
    {
      title: 'Choose your microphone and start',
      content:
        'The Settings panel on the right shows what the setup chose. Under <strong>Microphone</strong>, pick the microphone you speak into. Then click <strong>Start</strong> at the bottom of the window. If Start stays grey, hover over it: the note tells you what is still missing.',
      screenshot: '09-main-window.png',
      tip: 'Both mode also needs Other’s audio to be set, so that Sokuji knows which app the other side is speaking from. The two-way meeting guide covers this. Face-to-face does not: the other person shares your microphone.',
    },
    {
      title: 'Check your balance and top up',
      content:
        'The account button in the title bar shows your balance. Click it to see your email address, your balance and what you spent in the last 30 days. <strong>Top up</strong> opens the billing page of your Kizuna AI account in your browser, where you add funds and can see the current rates before you pay.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'Switch the engine (optional)',
      content:
        'KizunaAI runs on one of two engines, and the AI service provider list shows both: <strong>KizunaAI — Powered by Soniox</strong>, the recommended default the setup chose, and <strong>KizunaAI — Powered by Qwen</strong>. To change, open <strong>Settings</strong>, switch to <strong>Advanced</strong> and pick the other one under <strong>Provider</strong>; the choice applies from the next session. The FAQ below says how the two differ.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: 'Tips for Best Results',
    items: [
      'Use headphones. Without them, your microphone can pick up the translated voice and translate it again.',
      'When you only need subtitles, switch off Translation the other side hears in the Speech block of Settings and leave Translation I hear off. Generating speech costs extra.',
      'Stop the session when you are done: a running session keeps using your balance.',
      'A red dot on the account button means the balance is too low to start a session; an amber dot means your email address is not verified yet.',
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'What does it cost?',
        answer:
          'Translation is paid from a prepaid balance, by use, with no subscription. The rate depends on the engine and on what a session does (for example, spoken output costs more than subtitles only). The billing page of your Kizuna AI account lists the current rates before you top up.',
      },
      {
        question: 'What happens when my balance runs out?',
        answer:
          'Sokuji does not start a session while the balance is too low, and a running session stops when its balance is used up. Top up, then click Start again.',
      },
      {
        question: 'Where does my audio go?',
        answer:
          'Sokuji sends your audio straight to the engine you picked — Soniox, or Alibaba Cloud’s Qwen service in the region you chose — using a short-lived key issued for your account. Kizuna AI handles your account and billing. With screen context on (Qwen engine), pictures of the one window, screen or meeting tab in use go the same way; Kizuna AI never receives them.',
      },
      {
        question: 'Soniox or Qwen: which engine should I pick?',
        answer:
          'Soniox is the all-rounder: 60 languages, about 200 preset voices or a voice cloned from a recording, a shared session for Both mode, face-to-face conversation, and regions in the United States, the European Union and Japan. Qwen, from Alibaba Cloud, interprets speech to speech in one model, speaks 29 of its 60 languages, can clone your voice live as you talk with nothing to record, and runs in Singapore or Beijing. Both are paid from the same balance, at their own rates.',
      },
      {
        question: 'Can the Qwen engine see my slides?',
        answer:
          'Yes: turn on Screen context in the Qwen engine’s settings; in the desktop app you pick the window or screen when you turn it on, and the browser extension uses the meeting tab. It adds to the charge for every second of speech while it is on; the Qwen setup guide explains it.',
      },
      {
        question: 'How do I run the setup guide again?',
        answer: 'Open Settings, scroll to Help and click Run setup again.',
      },
    ],
  },
  troubleshooting: {
    title: 'Troubleshooting',
    content:
      'Still stuck? Send us feedback from the account menu, or write to <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. You can also ask in <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const zh: TutorialData = {
  pageTitle: 'KizunaAI 快速入门',
  backLink: '返回 AI 提供商',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: '概述',
    content:
      'KizunaAI 是上手 Sokuji 最快的方式：无需创建 API 密钥。你只需登录 Kizuna AI 账号，翻译由 Soniox 或 Qwen 这两个引擎之一完成，Kizuna AI 向引擎付费，并从你的余额中扣费。完成邮箱验证的账号可获得试用额度（目前为 US$0.50，有效期 7 天）；之后只需充值余额，按实际用量付费，无需订阅。本指南带你走完 Sokuji 桌面应用的首次启动流程。截图来自 Windows 上的 Sokuji 0.44.0，macOS 和 Linux 上的界面相同。',
  },
  steps: [
    {
      title: '安装 Sokuji',
      content:
        '从<a href="/download">下载页面</a>下载桌面应用并安装（参见 <a href="/docs/install/windows">Windows</a>、<a href="/docs/install/macos">macOS</a> 和 <a href="/docs/install/linux">Linux</a> 安装指南）。首次启动时，Sokuji 会打开一个六步的设置向导。',
      tip: '在 Windows 上，Sokuji 启动时会询问是否安装免费的虚拟音频驱动 VB-CABLE。如果希望通话对方听到你的翻译语音，请点击 Install Now。',
    },
    {
      title: '选择界面语言',
      content:
        '这里只是 Sokuji 菜单和按钮所用的语言，要互译的语言稍后再选。点击<strong>下一步</strong>。',
      screenshot: '01-language.png',
    },
    {
      title: '告诉 Sokuji 你想做什么',
      content:
        '选择最符合你使用方式的卡片。它决定翻译模式（<strong>我</strong>、<strong>对方</strong>或<strong>两者</strong>），以及翻译是朗读出来还是只显示字幕。<strong>听懂对方</strong>为你实时翻译听到的内容；<strong>让对方在会议里听懂我</strong>让对方听到你的翻译语音；<strong>在线双向对话</strong>同时做到这两点，而且当 Sokuji 收听你的会议应用时，还会把对方的翻译朗读给你听。两张<strong>面对面对话</strong>卡片适用于两个人在同一台电脑前交谈。这些之后都可以更改。',
      screenshot: '02-scenario.png',
      tip: '如果要和说另一种语言的人视频通话，请选择“在线双向对话”，然后参照双向会议指南操作。如果对方就坐在你身边，请选择“面对面对话”，然后参照面对面对话指南操作。',
    },
    {
      title: '选择“直接开始”',
      content:
        '选择<strong>直接开始</strong>。之后由 Kizuna AI 为你运行翻译，无需创建或粘贴 API 密钥；设置向导会选择 Soniox 引擎，之后你可以改用 Qwen（见下文）。点击<strong>下一步</strong>。',
      screenshot: '03-provider.png',
    },
    {
      title: '登录或注册账号',
      content:
        '如果你是 Kizuna AI 新用户，点击<strong>注册账号</strong>；如果已有账号，点击<strong>登录</strong>。',
      screenshot: '04-account.png',
    },
    {
      title: '填写注册表单',
      content:
        '输入你的姓名、邮箱地址和至少 8 个字符的密码，然后点击<strong>注册</strong>。同一个账号也可以登录 Kizuna AI 网站，你在那里管理余额。',
      screenshot: '05-create-account.png',
    },
    {
      title: '验证邮箱地址',
      content:
        'Sokuji 会立即为你登录，并向你的邮箱发送一封验证链接。打开该链接：邮箱验证通过后，试用额度就会计入你的余额。然后点击<strong>下一步</strong>。',
      screenshot: '06-verify-email.png',
      tip: '没收到邮件？请检查垃圾邮件文件夹。在此期间你可以先完成设置；一旦验证通过，额度就会显示在账号按钮上。',
    },
    {
      title: '选择语言',
      content:
        '<strong>我说</strong>是你的语言；<strong>对方听到</strong>是对方收到的语言。在双向模式下，反方向会自动设置好，下方那一行会显示它：此处为“对方说日语 → 我阅读英语”。当对方的翻译会朗读给你听时，这里会显示“我听到”。',
      screenshot: '07-languages.png',
      tip: '如果对方的话也需要翻译（对方或两者模式），请把“我说”设为具体语言，而不是“自动检测”。否则 Sokuji 无法判断要把对方的话翻译成哪种语言，“开始”按钮会一直不可用。',
    },
    {
      title: '确认并完成',
      content:
        '检查摘要后点击<strong>完成</strong>。接下来会有一段主窗口的简短导览。你可以跳过，之后随时在<strong>设置 → 帮助</strong>中重新播放。',
      screenshot: '08-ready.png',
    },
    {
      title: '选择麦克风并开始',
      content:
        '右侧的设置面板显示了向导所做的选择。在<strong>麦克风</strong>下选择你说话用的麦克风。然后点击窗口底部的<strong>开始</strong>。如果“开始”一直是灰色的，把鼠标悬停在上面：提示会告诉你还缺少什么。',
      screenshot: '09-main-window.png',
      tip: '两者模式还需要设置“对方音频”，让 Sokuji 知道对方的声音来自哪个应用。双向会议指南中有相关说明。面对面对话不需要：对方和你共用麦克风。',
    },
    {
      title: '查看余额并充值',
      content:
        '标题栏中的账号按钮会显示你的余额。点击它可以查看你的邮箱地址、余额以及最近 30 天的消费。<strong>充值</strong>会在浏览器中打开你 Kizuna AI 账号的账单页面，你可以在那里充值，并在付款前查看当前费率。',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: '切换引擎（可选）',
      content:
        'KizunaAI 运行在两个引擎之一上，AI 服务提供商列表中会同时显示这两项：<strong>KizunaAI — Soniox 驱动</strong>，即设置向导所选的推荐默认项；以及<strong>KizunaAI — Qwen 驱动</strong>。要更换，请打开<strong>设置</strong>，切换到<strong>高级</strong>，在<strong>提供商</strong>下选择另一个；该选择从下一次会话开始生效。下面的常见问题说明了两者的区别。',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: '最佳使用建议',
    items: [
      '请使用耳机。否则麦克风可能会拾取翻译后的语音并再次翻译。',
      '只需要字幕时，请在设置的“语音”部分关闭“对方听到的翻译”，并让“我听到的翻译”保持关闭。生成语音会额外计费。',
      '用完后请停止会话：正在运行的会话会持续消耗余额。',
      '账号按钮上的红点表示余额不足以开始会话；琥珀色圆点表示邮箱地址尚未验证。',
    ],
  },
  faq: {
    title: '常见问题',
    items: [
      {
        question: '费用是多少？',
        answer:
          '翻译费用从预付余额中按用量扣除，无需订阅。费率取决于引擎和会话的内容（例如，语音输出比仅字幕更贵）。充值前，你可以在 Kizuna AI 账号的账单页面查看当前费率。',
      },
      {
        question: '余额用完会怎样？',
        answer:
          '余额过低时，Sokuji 不会开始会话；正在运行的会话会在余额耗尽时停止。充值后再次点击“开始”即可。',
      },
      {
        question: '我的音频会发送到哪里？',
        answer:
          'Sokuji 使用为你的账号签发的短期密钥，将音频直接发送给你所选的引擎——Soniox，或你所选区域的阿里云 Qwen 服务。Kizuna AI 负责你的账号和计费。开启屏幕上下文时（Qwen 引擎），正在使用的那一个窗口、屏幕或会议标签页的画面也走同一条路；Kizuna AI 永远不会收到这些画面。',
      },
      {
        question: 'Soniox 还是 Qwen：我该选哪个引擎？',
        answer:
          'Soniox 是全能型：60 种语言，约 200 个预设声音或用录音克隆的声音，两者模式的共享会话，面对面对话，以及位于美国、欧盟和日本的区域。Qwen 来自阿里云，用一个模型直接完成语音到语音的传译，60 种语言中有 29 种可朗读，可以在你说话时实时克隆你的声音，无需录音，运行在新加坡或北京。两者都从同一笔余额中扣费，各按自己的费率。',
      },
      {
        question: 'Qwen 引擎能看到我的幻灯片吗？',
        answer:
          '可以：在 Qwen 引擎的设置中打开“屏幕上下文”；在桌面应用中，打开该开关时选择窗口或屏幕，浏览器扩展则使用会议标签页。开启期间，每一秒语音的费用都会增加；Qwen 设置教程中有说明。',
      },
      {
        question: '如何重新运行设置向导？',
        answer: '打开设置，滚动到“帮助”，点击“重新运行设置向导”。',
      },
    ],
  },
  troubleshooting: {
    title: '故障排除',
    content:
      '仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>。你也可以在 <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> 中提问。',
  },
};

const ja: TutorialData = {
  pageTitle: 'KizunaAI クイックスタート',
  backLink: 'AI プロバイダーに戻る',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: '概要',
    content:
      'KizunaAI は Sokuji をいちばん手早く使い始める方法です。API キーを作成する必要はありません。Kizuna AI アカウントでサインインするだけで、翻訳は Soniox と Qwen の 2 つのエンジンのどちらかで実行されます。エンジンの利用料は Kizuna AI が支払い、あなたの残高に請求されます。メールアドレスを認証したアカウントには試用クレジット（現在 US$0.50、有効期間 7 日）が付きます。その後は残高をチャージし、使った分だけ支払います。サブスクリプションはありません。このガイドでは、Sokuji デスクトップアプリの初回起動の流れを説明します。スクリーンショットは Windows 版 Sokuji 0.44.0 のものです。macOS と Linux でも画面は同じです。',
  },
  steps: [
    {
      title: 'Sokuji をインストール',
      content:
        '<a href="/download">ダウンロードページ</a>からデスクトップアプリをダウンロードしてインストールします（<a href="/docs/install/windows">Windows</a>、<a href="/docs/install/macos">macOS</a>、<a href="/docs/install/linux">Linux</a> の各ガイドを参照）。初回起動時に、Sokuji は 6 ステップのセットアップガイドを開きます。',
      tip: 'Windows では、Sokuji の起動時に無料の仮想オーディオドライバー VB-CABLE をインストールするか確認されます。通話相手に翻訳音声を聞かせたい場合は Install Now をクリックしてください。',
    },
    {
      title: '表示言語を選ぶ',
      content:
        'これは Sokuji のメニューやボタンの言語にすぎません。翻訳する言語はあとで選びます。<strong>次へ</strong>をクリックします。',
      screenshot: '01-language.png',
    },
    {
      title: '使い方を Sokuji に伝える',
      content:
        '使い方にいちばん近いカードを選びます。これにより翻訳モード（<strong>自分</strong>、<strong>相手</strong>、<strong>両方</strong>）と、翻訳を音声で読み上げるか字幕だけにするかが決まります。<strong>相手の話を理解する</strong>は聞こえてくる内容をリアルタイムで翻訳します。<strong>会議で自分の話を伝える</strong>は相手にあなたの翻訳音声を届けます。<strong>オンラインで双方向に会話</strong>はその両方を同時に行い、Sokuji が会議アプリの音声を聞き取るようになれば、相手の翻訳もあなたに読み上げます。2 枚の<strong>対面で会話</strong>カードは、1 台のパソコンを 2 人で使うためのものです。これらはすべてあとから変更できます。',
      screenshot: '02-scenario.png',
      tip: '別の言語を話す人とビデオ通話をする場合は「オンラインで双方向に会話」を選び、双方向会議ガイドに沿って進めてください。隣に座っている人と話す場合は「対面で会話」を選び、対面会話ガイドに沿って進めてください。',
    },
    {
      title: '「すぐに始める」を選ぶ',
      content:
        '<strong>すぐに始める</strong>を選びます。以降は Kizuna AI が翻訳を実行するので、API キーを作成したり貼り付けたりする必要はありません。セットアップでは Soniox エンジンが選ばれ、あとから Qwen に切り替えることもできます（下記参照）。<strong>次へ</strong>をクリックします。',
      screenshot: '03-provider.png',
    },
    {
      title: 'サインインまたはアカウント作成',
      content:
        'Kizuna AI を初めて使う場合は<strong>アカウント作成</strong>を、すでにアカウントをお持ちの場合は<strong>サインイン</strong>をクリックします。',
      screenshot: '04-account.png',
    },
    {
      title: '登録フォームに入力する',
      content:
        '名前、メールアドレス、8 文字以上のパスワードを入力し、<strong>サインアップ</strong>をクリックします。同じアカウントで Kizuna AI のウェブサイトにもサインインでき、残高はそちらで管理します。',
      screenshot: '05-create-account.png',
    },
    {
      title: 'メールアドレスを認証する',
      content:
        'Sokuji はすぐにサインインし、認証リンクをメールで送ります。そのリンクを開いてください。アドレスが認証されると、試用クレジットが残高に追加されます。その後<strong>次へ</strong>をクリックします。',
      screenshot: '06-verify-email.png',
      tip: 'メールが届かない場合は迷惑メールフォルダーを確認してください。その間にセットアップを完了しておいても構いません。認証が済むとすぐに、クレジットがアカウントボタンに表示されます。',
    },
    {
      title: '言語を選ぶ',
      content:
        '<strong>話す言語</strong>はあなたの言語、<strong>相手が聞く言語</strong>は相手に届く言語です。双方向モードでは逆方向が自動で設定され、その下の行に表示されます。この例では「相手は日本語を話す → 自分は英語で読む」です。相手の翻訳があなたに読み上げられる場合は、代わりに「相手は日本語を話す → 自分は英語で聞く」と表示されます。',
      screenshot: '07-languages.png',
      tip: '相手の発言も翻訳する場合（相手または両方モード）は、「話す言語」を「自動検出」ではなく具体的な言語に設定してください。そうしないと、Sokuji は相手の発言を何語に翻訳すればよいか判断できず、「開始」を押せないままになります。',
    },
    {
      title: '確認して完了する',
      content:
        '概要を確認して<strong>完了</strong>をクリックします。続いてメイン画面の短いツアーが表示されます。スキップしても、<strong>設定 → ヘルプ</strong>からいつでも再生できます。',
      screenshot: '08-ready.png',
    },
    {
      title: 'マイクを選んで開始する',
      content:
        '右側の設定パネルに、セットアップで選ばれた内容が表示されます。<strong>マイクロフォン</strong>で、話すときに使うマイクを選びます。次に、ウィンドウ下部の<strong>開始</strong>をクリックします。「開始」がグレーのままの場合は、マウスポインターを重ねてください。足りないものが表示されます。',
      screenshot: '09-main-window.png',
      tip: '両方モードでは「相手の音声」も設定する必要があります。これで Sokuji は、相手の声がどのアプリから聞こえてくるかを把握します。詳しくは双方向会議ガイドを参照してください。対面ではその必要はありません。相手はあなたのマイクを共有します。',
    },
    {
      title: '残高を確認してチャージする',
      content:
        'タイトルバーのアカウントボタンに残高が表示されます。クリックすると、メールアドレス、残高、過去 30 日間の利用額を確認できます。<strong>チャージ</strong>をクリックすると、ブラウザーで Kizuna AI アカウントの請求ページが開きます。そこで残高を追加でき、支払い前に現在の料金も確認できます。',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'エンジンを切り替える（任意）',
      content:
        'KizunaAI は 2 つのエンジンのどちらかで動き、AI サービスプロバイダーの一覧には両方が表示されます。セットアップが選んだ推奨の既定が <strong>KizunaAI — Soniox 提供</strong>、もう 1 つが <strong>KizunaAI — Qwen 提供</strong>です。切り替えるには、<strong>設定</strong>を開き、<strong>詳細</strong>に切り替えて、<strong>プロバイダー</strong>でもう一方を選びます。選択は次のセッションから適用されます。2 つの違いは下の「よくある質問」で説明しています。',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: '最良の結果を得るためのヒント',
    items: [
      'ヘッドホンを使ってください。使わないと、マイクが翻訳音声を拾ってもう一度翻訳してしまうことがあります。',
      '字幕だけでよい場合は、設定の「音声」セクションで「相手に聞こえる翻訳」をオフにし、「自分に聞こえる翻訳」もオフのままにしてください。音声の生成には追加料金がかかります。',
      '使い終わったらセッションを停止してください。実行中のセッションは残高を消費し続けます。',
      'アカウントボタンの赤い点は残高不足でセッションを開始できないこと、オレンジ色の点はメールアドレスが未認証であることを示します。',
    ],
  },
  faq: {
    title: 'よくある質問',
    items: [
      {
        question: '料金はいくらですか？',
        answer:
          '翻訳料金はプリペイド残高から使った分だけ差し引かれ、サブスクリプションはありません。料金はエンジンと、セッションの内容によって異なります（たとえば、音声出力は字幕のみより高くなります）。チャージする前に、Kizuna AI アカウントの請求ページで現在の料金を確認できます。',
      },
      {
        question: '残高がなくなるとどうなりますか？',
        answer:
          '残高が少なすぎる間、Sokuji はセッションを開始しません。実行中のセッションは残高を使い切った時点で停止します。チャージしてから、もう一度「開始」をクリックしてください。',
      },
      {
        question: '音声はどこに送られますか？',
        answer:
          'Sokuji は、選んだエンジン（Soniox、または選んだリージョンにある Alibaba Cloud の Qwen サービス）に、あなたのアカウント用に発行された短期間だけ有効なキーを使って音声を直接送ります。Kizuna AI が扱うのはアカウントと請求です。画面コンテキストをオンにしている場合（Qwen エンジン）、使われている一つのウィンドウ・画面・会議タブの画像も同じ経路で送られ、Kizuna AI がそれを受け取ることはありません。',
      },
      {
        question: 'Soniox と Qwen、どちらのエンジンを選べばよいですか？',
        answer:
          'Soniox は万能型です。60 言語に対応し、約 200 種類のプリセット音声か録音から複製した声を使え、両方モードでセッションを共有でき、対面での会話にも対応し、リージョンは米国、欧州連合、日本から選べます。Alibaba Cloud の Qwen は、1 つのモデルで音声から音声へ通訳し、60 言語のうち 29 言語で話し、録音なしで話しながらあなたの声をその場で複製でき、シンガポールまたは北京で動作します。どちらも同じ残高から、それぞれの料金で支払います。',
      },
      {
        question: 'Qwen エンジンにスライドを見せられますか？',
        answer:
          'はい。Qwen エンジンの設定で「画面コンテキスト」をオンにします。デスクトップアプリではオンにするときにウィンドウまたは画面を選び、ブラウザ拡張機能では会議のタブが使われます。オンの間は発話の毎秒ぶんの料金が加算されます。詳しくは Qwen のセットアップガイドで説明しています。',
      },
      {
        question: 'セットアップガイドをもう一度実行するには？',
        answer: '設定を開き、「ヘルプ」までスクロールして「セットアップをやり直す」をクリックします。',
      },
    ],
  },
  troubleshooting: {
    title: 'トラブルシューティング',
    content:
      'それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までご連絡ください。<a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> で質問することもできます。',
  },
};

const ko: TutorialData = {
  pageTitle: 'KizunaAI 빠른 시작',
  backLink: 'AI 제공업체로 돌아가기',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: '개요',
    content:
      'KizunaAI는 Sokuji를 가장 빠르게 시작하는 방법입니다. API 키를 만들 필요가 없습니다. Kizuna AI 계정으로 로그인하기만 하면 번역은 Soniox와 Qwen 두 엔진 중 하나에서 실행되며, 엔진 사용료는 Kizuna AI가 대신 내고 내 잔액에서 차감합니다. 이메일 주소를 인증한 계정에는 체험 크레딧(현재 US$0.50, 7일간 유효)이 제공됩니다. 그 이후에는 잔액을 충전하고 사용한 만큼만 지불하며, 구독은 없습니다. 이 가이드는 Sokuji 데스크톱 앱의 첫 실행 과정을 안내합니다. 스크린샷은 Windows용 Sokuji 0.44.0에서 촬영했으며, macOS와 Linux에서도 화면은 같습니다.',
  },
  steps: [
    {
      title: 'Sokuji 설치',
      content:
        '<a href="/download">다운로드 페이지</a>에서 데스크톱 앱을 내려받아 설치합니다(<a href="/docs/install/windows">Windows</a>, <a href="/docs/install/macos">macOS</a>, <a href="/docs/install/linux">Linux</a> 가이드 참조). 처음 실행하면 Sokuji가 6단계 설정 가이드를 엽니다.',
      tip: 'Windows에서는 Sokuji가 시작될 때 무료 가상 오디오 드라이버인 VB-CABLE을 설치할지 묻습니다. 통화 상대방이 번역된 내 음성을 듣게 하려면 Install Now를 클릭하세요.',
    },
    {
      title: '인터페이스 언어 선택',
      content:
        '이것은 Sokuji의 메뉴와 버튼에 쓰이는 언어일 뿐입니다. 번역할 언어는 나중에 선택합니다. <strong>다음</strong>을 클릭하세요.',
      screenshot: '01-language.png',
    },
    {
      title: '무엇을 하고 싶은지 Sokuji에 알려주기',
      content:
        'Sokuji를 사용할 방식과 가장 가까운 카드를 고르세요. 이 선택으로 번역 모드(<strong>나</strong>, <strong>상대방</strong> 또는 <strong>모두</strong>)와 번역을 음성으로 들려줄지 자막으로만 보여줄지가 정해집니다. <strong>상대방의 말 이해하기</strong>는 들리는 내용을 실시간으로 번역해 줍니다. <strong>회의에서 내 말 전달하기</strong>는 상대방이 번역된 내 음성을 듣게 합니다. <strong>양방향 온라인 대화</strong>는 두 가지를 동시에 하며, Sokuji가 회의 앱의 소리를 듣게 되면 상대방의 번역도 소리 내어 읽어 줍니다. 두 개의 <strong>마주 보고 대화</strong> 카드는 한 대의 컴퓨터 앞에 있는 두 사람을 위한 것입니다. 이 모든 것은 나중에 변경할 수 있습니다.',
      screenshot: '02-scenario.png',
      tip: '다른 언어를 쓰는 사람과 화상 통화를 하려면 “양방향 온라인 대화”를 선택한 다음 양방향 회의 가이드를 따르세요. 옆에 앉은 사람과 대화하려면 “마주 보고 대화”를 선택한 다음 마주 보고 대화 가이드를 따르세요.',
    },
    {
      title: '“바로 시작하기” 선택',
      content:
        '<strong>바로 시작하기</strong>를 선택하세요. 그러면 Kizuna AI가 번역을 대신 처리하므로 API 키를 만들거나 붙여 넣을 필요가 없습니다. 설정 가이드는 Soniox 엔진을 선택하며, 나중에 Qwen으로 바꿀 수 있습니다(아래 참조). <strong>다음</strong>을 클릭하세요.',
      screenshot: '03-provider.png',
    },
    {
      title: '로그인 또는 계정 만들기',
      content:
        'Kizuna AI를 처음 사용한다면 <strong>회원가입</strong>을, 이미 계정이 있다면 <strong>로그인</strong>을 클릭하세요.',
      screenshot: '04-account.png',
    },
    {
      title: '가입 양식 작성',
      content:
        '이름, 이메일 주소, 8자 이상의 비밀번호를 입력한 다음 <strong>회원가입</strong>을 클릭하세요. 같은 계정으로 Kizuna AI 웹사이트에도 로그인할 수 있으며, 잔액은 그곳에서 관리합니다.',
      screenshot: '05-create-account.png',
    },
    {
      title: '이메일 주소 인증',
      content:
        'Sokuji가 바로 로그인해 주고 인증 링크를 이메일로 보냅니다. 그 링크를 여세요. 주소가 인증되면 체험 크레딧이 잔액에 추가됩니다. 그런 다음 <strong>다음</strong>을 클릭하세요.',
      screenshot: '06-verify-email.png',
      tip: '이메일이 오지 않았나요? 스팸 폴더를 확인하세요. 그동안 설정을 먼저 마쳐도 됩니다. 인증하는 즉시 크레딧이 계정 버튼에 표시됩니다.',
    },
    {
      title: '언어 선택',
      content:
        '<strong>내가 말하는 언어</strong>는 나의 언어이고, <strong>상대방이 듣는 언어</strong>는 상대방에게 전달되는 언어입니다. 양방향 모드에서는 반대 방향이 자동으로 설정되며, 아래 줄에 표시됩니다. 여기서는 “상대방은 일본어로 말함 → 나는 영어로 읽음”입니다. 상대방의 번역을 소리 내어 읽어 줄 때는 “상대방은 일본어로 말함 → 나는 영어로 들음”으로 표시됩니다.',
      screenshot: '07-languages.png',
      tip: '상대방의 말도 번역하는 경우(상대방 또는 모두 모드)에는 “내가 말하는 언어”를 “자동 감지”가 아닌 특정 언어로 설정하세요. 그렇지 않으면 Sokuji가 상대방의 말을 어떤 언어로 번역해야 할지 알 수 없어 “시작”이 계속 비활성화됩니다.',
    },
    {
      title: '확인 후 완료',
      content:
        '요약을 확인하고 <strong>완료</strong>를 클릭하세요. 이어서 메인 창을 간단히 둘러보는 투어가 나옵니다. 건너뛰어도 <strong>설정 → 도움말</strong>에서 언제든 다시 볼 수 있습니다.',
      screenshot: '08-ready.png',
    },
    {
      title: '마이크를 선택하고 시작',
      content:
        '오른쪽 설정 패널에 설정 가이드가 선택한 내용이 표시됩니다. <strong>마이크</strong>에서 말할 때 사용할 마이크를 고르세요. 그런 다음 창 아래쪽의 <strong>시작</strong>을 클릭하세요. “시작”이 계속 회색이면 마우스를 올려 보세요. 아직 무엇이 빠졌는지 알려 줍니다.',
      screenshot: '09-main-window.png',
      tip: '모두 모드에서는 “상대방 오디오”도 설정해야 Sokuji가 상대방의 목소리가 어느 앱에서 나오는지 알 수 있습니다. 자세한 내용은 양방향 회의 가이드에서 다룹니다. 마주 보고 대화할 때는 필요 없습니다. 상대방이 내 마이크를 함께 사용하기 때문입니다.',
    },
    {
      title: '잔액 확인 및 충전',
      content:
        '제목 표시줄의 계정 버튼에 잔액이 표시됩니다. 클릭하면 이메일 주소, 잔액, 최근 30일간 사용한 금액을 볼 수 있습니다. <strong>충전</strong>을 클릭하면 브라우저에서 Kizuna AI 계정의 결제 페이지가 열리며, 여기서 금액을 충전하고 결제 전에 현재 요금을 확인할 수 있습니다.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: '엔진 바꾸기(선택 사항)',
      content:
        'KizunaAI는 두 엔진 중 하나로 실행되며, AI 서비스 제공자 목록에 두 가지가 모두 표시됩니다. 설정 가이드가 선택한 권장 기본값인 <strong>KizunaAI — Soniox 제공</strong>과 <strong>KizunaAI — Qwen 제공</strong>입니다. 바꾸려면 <strong>설정</strong>을 열고 <strong>고급</strong>으로 전환한 다음 <strong>제공자</strong> 탭에서 다른 쪽을 선택하세요. 선택은 다음 세션부터 적용됩니다. 두 엔진의 차이는 아래 자주 묻는 질문에서 설명합니다.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: '최상의 결과를 위한 팁',
    items: [
      '헤드폰을 사용하세요. 그렇지 않으면 마이크가 번역된 음성을 다시 받아 한 번 더 번역할 수 있습니다.',
      '자막만 필요할 때는 설정의 “음성” 섹션에서 “상대방에게 들리는 번역”을 끄고, “내가 듣는 번역”은 꺼 둔 채로 두세요. 음성 생성에는 추가 비용이 듭니다.',
      '다 쓰고 나면 세션을 중지하세요. 실행 중인 세션은 계속 잔액을 사용합니다.',
      '계정 버튼의 빨간 점은 잔액이 부족해 세션을 시작할 수 없다는 뜻이고, 주황색 점은 이메일 주소가 아직 인증되지 않았다는 뜻입니다.',
    ],
  },
  faq: {
    title: '자주 묻는 질문',
    items: [
      {
        question: '비용은 얼마인가요?',
        answer:
          '번역 비용은 선불 잔액에서 사용량만큼 차감되며, 구독은 없습니다. 요금은 엔진과 세션에서 무엇을 하느냐에 따라 달라집니다(예: 음성 출력은 자막만 사용할 때보다 비쌉니다). 충전하기 전에 Kizuna AI 계정의 결제 페이지에서 현재 요금을 확인할 수 있습니다.',
      },
      {
        question: '잔액이 다 떨어지면 어떻게 되나요?',
        answer:
          '잔액이 너무 적으면 Sokuji는 세션을 시작하지 않으며, 실행 중인 세션은 잔액을 모두 쓰면 중지됩니다. 충전한 다음 “시작”을 다시 클릭하세요.',
      },
      {
        question: '내 오디오는 어디로 가나요?',
        answer:
          'Sokuji는 내 계정용으로 발급된 단기 키를 사용해 오디오를 내가 고른 엔진(Soniox 또는 내가 선택한 지역의 알리바바 클라우드 Qwen 서비스)으로 직접 보냅니다. Kizuna AI는 계정과 결제를 담당합니다. 화면 컨텍스트를 켜면(Qwen 엔진) 사용 중인 창, 화면 또는 회의 탭 하나의 이미지도 같은 경로로 전송되며, Kizuna AI는 이를 절대 받지 않습니다.',
      },
      {
        question: 'Soniox와 Qwen 중 어느 엔진을 골라야 하나요?',
        answer:
          'Soniox는 두루 갖춘 엔진입니다. 60개 언어, 약 200개의 프리셋 음성 또는 녹음으로 복제한 음성, 모두 모드에서 쓰는 공유 세션, 마주 보고 대화, 그리고 미국, 유럽 연합, 일본 지역을 지원합니다. 알리바바 클라우드의 Qwen은 하나의 모델로 음성을 음성으로 통역하고, 60개 언어 중 29개 언어로 말하며, 녹음할 필요 없이 말하는 동안 내 목소리를 실시간으로 복제할 수 있고, 싱가포르 또는 베이징에서 실행됩니다. 두 엔진 모두 같은 잔액에서 각자의 요금으로 결제됩니다.',
      },
      {
        question: 'Qwen 엔진이 내 슬라이드를 볼 수 있나요?',
        answer:
          '네. Qwen 엔진 설정에서 “화면 컨텍스트”를 켜세요. 데스크톱 앱에서는 켤 때 창이나 화면을 고르고, 브라우저 확장 프로그램에서는 회의 탭이 사용됩니다. 켜져 있는 동안 음성 한 초마다 요금이 추가되며, 자세한 내용은 Qwen 설정 가이드에 있습니다.',
      },
      {
        question: '설정 가이드를 다시 실행하려면 어떻게 하나요?',
        answer: '설정을 열고 “도움말”까지 스크롤한 다음 “설정 다시 실행”을 클릭하세요.',
      },
    ],
  },
  troubleshooting: {
    title: '문제 해결',
    content:
      '그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>에서 질문할 수도 있습니다.',
  },
};

const es: TutorialData = {
  pageTitle: 'Inicio rápido de KizunaAI',
  backLink: 'Volver a proveedores de IA',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: 'Descripción general',
    content:
      'KizunaAI es la forma más rápida de empezar con Sokuji: no hay que crear ninguna clave API. Inicias sesión con una cuenta de Kizuna AI, y la traducción se ejecuta en uno de dos motores, Soniox o Qwen, que Kizuna AI paga y carga a tu saldo. Las cuentas con una dirección de correo verificada reciben un crédito de prueba (actualmente 0,50 US$, válido durante 7 días); después recargas tu saldo y pagas solo lo que usas, sin suscripción. Esta guía recorre el primer inicio de la aplicación de escritorio de Sokuji. Las capturas son de Sokuji 0.44.0 en Windows; en macOS y Linux se ve igual.',
  },
  steps: [
    {
      title: 'Instala Sokuji',
      content:
        'Descarga la aplicación de escritorio desde la <a href="/download">página de descarga</a> e instálala (consulta las guías para <a href="/docs/install/windows">Windows</a>, <a href="/docs/install/macos">macOS</a> y <a href="/docs/install/linux">Linux</a>). La primera vez que lo abres, Sokuji muestra una guía de configuración de seis pasos.',
      tip: 'En Windows, Sokuji ofrece instalar VB-CABLE, un controlador de audio virtual gratuito, al arrancar. Haz clic en Install Now si quieres que la otra persona de la llamada oiga tu voz traducida.',
    },
    {
      title: 'Elige el idioma de la interfaz',
      content:
        'Es solo el idioma de los menús y botones de Sokuji. Los idiomas entre los que traducir los eliges más adelante. Haz clic en <strong>Siguiente</strong>.',
      screenshot: '01-language.png',
    },
    {
      title: 'Dile a Sokuji qué quieres hacer',
      content:
        'Elige la tarjeta que más se parezca a cómo vas a usar Sokuji. Define el modo de traducción (<strong>Yo</strong>, <strong>Otro</strong> o <strong>Ambos</strong>) y si las traducciones se leen en voz alta o se muestran solo como subtítulos. <strong>Entender lo que dicen los demás</strong> te da una traducción en directo de lo que oyes; <strong>Que te entiendan en una reunión</strong> hace que la otra parte oiga tu voz traducida; <strong>Conversación en línea bidireccional</strong> hace ambas cosas a la vez y, en cuanto Sokuji escucha tu aplicación de reuniones, también te lee en voz alta la traducción de la otra parte. Las dos tarjetas <strong>Conversación cara a cara</strong> son para dos personas ante un mismo ordenador. Puedes cambiar todo esto más adelante.',
      screenshot: '02-scenario.png',
      tip: 'Para una videollamada con alguien que habla otro idioma, elige Conversación en línea bidireccional y después sigue la guía de reuniones bidireccionales. Para alguien sentado a tu lado, elige Conversación cara a cara y sigue la guía de conversaciones cara a cara.',
    },
    {
      title: 'Elige «Empieza enseguida»',
      content:
        'Elige <strong>Empieza enseguida</strong>. Kizuna AI se encarga entonces de la traducción, así que no hay ninguna clave API que crear ni pegar; la configuración elige el motor de Soniox, y más adelante puedes cambiar a Qwen (consulta más abajo). Haz clic en <strong>Siguiente</strong>.',
      screenshot: '03-provider.png',
    },
    {
      title: 'Inicia sesión o crea una cuenta',
      content:
        'Haz clic en <strong>Crear cuenta</strong> si eres nuevo en Kizuna AI, o en <strong>Iniciar sesión</strong> si ya tienes una cuenta.',
      screenshot: '04-account.png',
    },
    {
      title: 'Rellena el formulario de registro',
      content:
        'Introduce tu nombre, tu dirección de correo y una contraseña de al menos 8 caracteres, y haz clic en <strong>Registrarse</strong>. Con la misma cuenta inicias sesión también en el sitio web de Kizuna AI, donde gestionas tu saldo.',
      screenshot: '05-create-account.png',
    },
    {
      title: 'Verifica tu dirección de correo',
      content:
        'Sokuji inicia tu sesión al momento y te envía un enlace de verificación por correo. Abre ese enlace: el crédito de prueba se añade a tu saldo en cuanto se verifica tu dirección. Después haz clic en <strong>Siguiente</strong>.',
      screenshot: '06-verify-email.png',
      tip: '¿No te ha llegado el correo? Revisa la carpeta de spam. Mientras tanto puedes terminar la configuración; el crédito aparece en el botón de la cuenta en cuanto verificas.',
    },
    {
      title: 'Elige tus idiomas',
      content:
        '<strong>Yo hablo</strong> es tu idioma; <strong>ellos escuchan</strong> es el idioma que recibe la otra parte. En el modo bidireccional, la dirección inversa se configura sola, y la línea de debajo la muestra: aquí, «Ellos hablan Japonés → yo leo Inglés». Cuando la traducción de la otra parte se te vaya a leer en voz alta, dice «yo escucho» en su lugar.',
      screenshot: '07-languages.png',
      tip: 'Si también se traduce lo que dice la otra parte (modo Otro o Ambos), pon en Yo hablo un idioma concreto en lugar de Detección Automática. Si no, Sokuji no sabe a qué idioma traducirles, e Iniciar sigue desactivado.',
    },
    {
      title: 'Revisa y termina',
      content:
        'Comprueba el resumen y haz clic en <strong>Finalizar</strong>. A continuación aparece un breve recorrido por la ventana principal. Puedes saltártelo y repetirlo cuando quieras desde <strong>Configuración → Ayuda</strong>.',
      screenshot: '08-ready.png',
    },
    {
      title: 'Elige tu micrófono y empieza',
      content:
        'El panel de Configuración de la derecha muestra lo que eligió la configuración. En <strong>Micrófono</strong>, elige el micrófono por el que hablas. Luego haz clic en <strong>Iniciar</strong>, en la parte inferior de la ventana. Si Iniciar sigue en gris, pasa el ratón por encima: la nota te dice qué falta todavía.',
      screenshot: '09-main-window.png',
      tip: 'El modo Ambos también necesita que Audio del otro esté configurado, para que Sokuji sepa desde qué aplicación habla la otra parte. La guía de reuniones bidireccionales lo explica. El modo cara a cara no lo necesita: la otra persona comparte tu micrófono.',
    },
    {
      title: 'Consulta tu saldo y recarga',
      content:
        'El botón de la cuenta, en la barra de título, muestra tu saldo. Haz clic en él para ver tu dirección de correo, tu saldo y lo que has gastado en los últimos 30 días. <strong>Recargar</strong> abre en tu navegador la página de facturación de tu cuenta de Kizuna AI, donde añades fondos y puedes ver las tarifas actuales antes de pagar.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'Cambia de motor (opcional)',
      content:
        'KizunaAI funciona con uno de dos motores, y la lista de proveedores de servicios de IA muestra ambos: <strong>KizunaAI — Con tecnología de Soniox</strong>, el predeterminado recomendado que eligió la configuración, y <strong>KizunaAI — Con tecnología de Qwen</strong>. Para cambiar, abre <strong>Configuración</strong>, cambia a <strong>Avanzado</strong> y elige el otro en <strong>Proveedor</strong>; la elección se aplica a partir de la siguiente sesión. Las preguntas frecuentes de abajo explican en qué se diferencian.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: 'Consejos para obtener mejores resultados',
    items: [
      'Usa auriculares. Sin ellos, tu micrófono puede captar la voz traducida y volver a traducirla.',
      'Cuando solo necesites subtítulos, desactiva Traducción que oye la otra parte en la sección Voz de Configuración y deja desactivada Traducción que yo oigo. Generar voz cuesta más.',
      'Detén la sesión cuando termines: una sesión en marcha sigue consumiendo tu saldo.',
      'Un punto rojo en el botón de la cuenta significa que el saldo es demasiado bajo para iniciar una sesión; un punto ámbar, que tu dirección de correo aún no está verificada.',
    ],
  },
  faq: {
    title: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Cuánto cuesta?',
        answer:
          'La traducción se paga con un saldo prepago, según el uso y sin suscripción. La tarifa depende del motor y de lo que haga cada sesión (por ejemplo, la salida hablada cuesta más que solo subtítulos). La página de facturación de tu cuenta de Kizuna AI muestra las tarifas actuales antes de que recargues.',
      },
      {
        question: '¿Qué pasa cuando se me acaba el saldo?',
        answer:
          'Sokuji no inicia una sesión mientras el saldo sea demasiado bajo, y una sesión en marcha se detiene cuando agota su saldo. Recarga y vuelve a hacer clic en Iniciar.',
      },
      {
        question: '¿Adónde va mi audio?',
        answer:
          'Sokuji envía tu audio directamente al motor que elegiste —Soniox, o el servicio Qwen de Alibaba Cloud en la región que elegiste—, con una clave de corta duración emitida para tu cuenta. Kizuna AI se ocupa de tu cuenta y de la facturación. Con el contexto de pantalla activado (motor de Qwen), las imágenes de la ventana, pantalla o pestaña de la reunión en uso siguen el mismo camino; Kizuna AI nunca las recibe.',
      },
      {
        question: 'Soniox o Qwen: ¿qué motor elijo?',
        answer:
          'Soniox es el más completo: 60 idiomas, unas 200 voces predefinidas o una voz clonada a partir de una grabación, una sesión compartida para el modo Ambos, conversación cara a cara y regiones en Estados Unidos, la Unión Europea y Japón. Qwen, de Alibaba Cloud, interpreta de voz a voz en un solo modelo, habla 29 de sus 60 idiomas, puede clonar tu voz en vivo mientras hablas sin nada que grabar, y funciona en Singapur o Pekín. Ambos se pagan con el mismo saldo, a sus propias tarifas.',
      },
      {
        question: '¿Puede el motor de Qwen ver mis diapositivas?',
        answer:
          'Sí: activa Contexto de pantalla en la configuración del motor de Qwen; en la aplicación de escritorio eliges la ventana o la pantalla cuando lo activas, y la extensión del navegador usa la pestaña de la reunión. Mientras está activado, aumenta el cargo por cada segundo de voz; la guía de configuración de Qwen lo explica.',
      },
      {
        question: '¿Cómo vuelvo a abrir la guía de configuración?',
        answer: 'Abre Configuración, desplázate hasta Ayuda y haz clic en Configurar de nuevo.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solución de problemas',
    content:
      '¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. También puedes preguntar en <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const fr: TutorialData = {
  pageTitle: 'Démarrage rapide KizunaAI',
  backLink: 'Retour aux fournisseurs IA',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: 'Aperçu',
    content:
      'KizunaAI est le moyen le plus rapide de commencer avec Sokuji : aucune clé API à créer. Vous vous connectez avec un compte Kizuna AI, et la traduction s’exécute sur l’un de deux moteurs, Soniox ou Qwen, que Kizuna AI paie et facture sur votre solde. Les comptes dont l’adresse e-mail est vérifiée reçoivent un crédit d’essai (actuellement 0,50 $ US, valable 7 jours) ; ensuite, vous rechargez votre solde et ne payez que ce que vous utilisez, sans abonnement. Ce guide décrit le premier lancement de l’application de bureau Sokuji. Les captures d’écran proviennent de Sokuji 0.44.0 sous Windows ; macOS et Linux ont le même aspect.',
  },
  steps: [
    {
      title: 'Installez Sokuji',
      content:
        'Téléchargez l’application de bureau depuis la <a href="/download">page de téléchargement</a> et installez-la (voir les guides <a href="/docs/install/windows">Windows</a>, <a href="/docs/install/macos">macOS</a> et <a href="/docs/install/linux">Linux</a>). Au premier lancement, Sokuji ouvre un guide de configuration en six étapes.',
      tip: 'Sous Windows, Sokuji propose au démarrage d’installer VB-CABLE, un pilote audio virtuel gratuit. Cliquez sur Install Now si vous voulez que votre interlocuteur entende votre voix traduite pendant l’appel.',
    },
    {
      title: 'Choisissez la langue de l’interface',
      content:
        'Il s’agit uniquement de la langue des menus et des boutons de Sokuji. Vous choisirez plus tard les langues entre lesquelles traduire. Cliquez sur <strong>Suivant</strong>.',
      screenshot: '01-language.png',
    },
    {
      title: 'Indiquez à Sokuji ce que vous voulez faire',
      content:
        'Choisissez la carte qui correspond le mieux à votre usage de Sokuji. Elle détermine le mode de traduction (<strong>Moi</strong>, <strong>Autre</strong> ou <strong>Les deux</strong>) et si les traductions sont prononcées ou seulement affichées en sous-titres. <strong>Comprendre ce que disent les autres</strong> vous donne une traduction en direct de ce que vous entendez ; <strong>Être compris en réunion</strong> permet à l’autre partie d’entendre votre voix traduite ; <strong>Conversation en ligne bidirectionnelle</strong> fait les deux à la fois et, dès que Sokuji écoute votre application de réunion, vous lit aussi à voix haute la traduction de l’autre partie. Les deux cartes <strong>Conversation en face à face</strong> sont destinées à deux personnes devant un même ordinateur. Vous pourrez modifier tout cela plus tard.',
      screenshot: '02-scenario.png',
      tip: 'Pour un appel vidéo avec quelqu’un qui parle une autre langue, choisissez Conversation en ligne bidirectionnelle, puis suivez le guide des réunions bidirectionnelles. Pour une personne assise à côté de vous, choisissez Conversation en face à face et suivez le guide de la conversation en face à face.',
    },
    {
      title: 'Choisissez « Commencez tout de suite »',
      content:
        'Choisissez <strong>Commencez tout de suite</strong>. Kizuna AI assure alors la traduction pour vous : aucune clé API à créer ni à coller ; la configuration choisit le moteur Soniox, et vous pourrez passer à Qwen plus tard (voir ci-dessous). Cliquez sur <strong>Suivant</strong>.',
      screenshot: '03-provider.png',
    },
    {
      title: 'Connectez-vous ou créez un compte',
      content:
        'Cliquez sur <strong>Créer un compte</strong> si vous découvrez Kizuna AI, ou sur <strong>Se connecter</strong> si vous avez déjà un compte.',
      screenshot: '04-account.png',
    },
    {
      title: 'Remplissez le formulaire d’inscription',
      content:
        'Saisissez votre nom, votre adresse e-mail et un mot de passe d’au moins 8 caractères, puis cliquez sur <strong>S\'inscrire</strong>. Le même compte vous connecte aussi au site web de Kizuna AI, où vous gérez votre solde.',
      screenshot: '05-create-account.png',
    },
    {
      title: 'Vérifiez votre adresse e-mail',
      content:
        'Sokuji vous connecte immédiatement et vous envoie un lien de vérification par e-mail. Ouvrez ce lien : le crédit d’essai est ajouté à votre solde dès que votre adresse est vérifiée. Cliquez ensuite sur <strong>Suivant</strong>.',
      screenshot: '06-verify-email.png',
      tip: 'Pas d’e-mail ? Vérifiez votre dossier de courrier indésirable. Vous pouvez terminer la configuration en attendant ; le crédit apparaît sur le bouton du compte dès que vous avez vérifié votre adresse.',
    },
    {
      title: 'Choisissez vos langues',
      content:
        '<strong>je parle</strong> est votre langue ; <strong>ils entendent</strong> est la langue que reçoit l’autre partie. En mode bidirectionnel, le sens inverse est réglé automatiquement, et la ligne en dessous l’indique : ici, « Ils parlent Japonais → je lis Anglais ». Elle indique « j\'entends » à la place quand la traduction de l’autre partie vous sera lue à voix haute.',
      screenshot: '07-languages.png',
      tip: 'Si les paroles de l’autre partie sont aussi traduites (mode Autre ou Les deux), réglez je parle sur une langue précise plutôt que sur Détection Automatique. Sinon, Sokuji ne sait pas dans quelle langue les traduire, et Démarrer reste désactivé.',
    },
    {
      title: 'Vérifiez et terminez',
      content:
        'Vérifiez le récapitulatif et cliquez sur <strong>Terminer</strong>. Une courte visite de la fenêtre principale suit. Vous pouvez la passer et la revoir à tout moment depuis <strong>Paramètres → Aide</strong>.',
      screenshot: '08-ready.png',
    },
    {
      title: 'Choisissez votre microphone et démarrez',
      content:
        'Le panneau Paramètres à droite montre ce que la configuration a choisi. Sous <strong>Microphone</strong>, sélectionnez le microphone dans lequel vous parlez. Cliquez ensuite sur <strong>Démarrer</strong> en bas de la fenêtre. Si Démarrer reste grisé, survolez-le : la note vous indique ce qui manque encore.',
      screenshot: '09-main-window.png',
      tip: 'Le mode Les deux nécessite aussi de régler Audio de l\'autre, pour que Sokuji sache depuis quelle application parle l’autre partie. Le guide des réunions bidirectionnelles explique comment faire. Ce n’est pas le cas du face à face : l’autre personne partage votre microphone.',
    },
    {
      title: 'Consultez votre solde et rechargez',
      content:
        'Le bouton du compte, dans la barre de titre, affiche votre solde. Cliquez dessus pour voir votre adresse e-mail, votre solde et vos dépenses des 30 derniers jours. <strong>Recharger</strong> ouvre dans votre navigateur la page de facturation de votre compte Kizuna AI, où vous ajoutez des fonds et pouvez consulter les tarifs en vigueur avant de payer.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'Changez de moteur (facultatif)',
      content:
        'KizunaAI fonctionne avec l’un de deux moteurs, et la liste des fournisseurs de services d’IA les affiche tous les deux : <strong>KizunaAI — Propulsé par Soniox</strong>, le choix recommandé par défaut que la configuration a retenu, et <strong>KizunaAI — Propulsé par Qwen</strong>. Pour changer, ouvrez les <strong>Paramètres</strong>, passez en <strong>Avancé</strong> et choisissez l’autre sous <strong>Fournisseur</strong> ; le choix s’applique à partir de la session suivante. La FAQ ci-dessous explique en quoi les deux diffèrent.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: 'Conseils pour de meilleurs résultats',
    items: [
      'Utilisez un casque. Sans casque, votre microphone peut capter la voix traduite et la traduire à nouveau.',
      'Lorsque vous n’avez besoin que de sous-titres, désactivez « Traduction entendue par l\'autre » dans la section Voix des Paramètres et laissez « Traduction que j\'entends » désactivée. La génération de la voix coûte plus cher.',
      'Arrêtez la session quand vous avez fini : une session en cours continue de consommer votre solde.',
      'Un point rouge sur le bouton du compte signifie que le solde est trop faible pour démarrer une session ; un point orange signifie que votre adresse e-mail n’est pas encore vérifiée.',
    ],
  },
  faq: {
    title: 'Questions fréquentes',
    items: [
      {
        question: 'Combien ça coûte ?',
        answer:
          'La traduction est payée à l’usage sur un solde prépayé, sans abonnement. Le tarif dépend du moteur et de ce que fait une session (par exemple, la sortie vocale coûte plus cher que les sous-titres seuls). La page de facturation de votre compte Kizuna AI indique les tarifs en vigueur avant que vous ne rechargiez.',
      },
      {
        question: 'Que se passe-t-il quand mon solde est épuisé ?',
        answer:
          'Sokuji ne démarre pas de session tant que le solde est trop faible, et une session en cours s’arrête quand son solde est épuisé. Rechargez, puis cliquez à nouveau sur Démarrer.',
      },
      {
        question: 'Où va mon audio ?',
        answer:
          'Sokuji envoie votre audio directement au moteur que vous avez choisi — Soniox, ou le service Qwen d’Alibaba Cloud dans la région choisie — avec une clé à courte durée de vie émise pour votre compte. Kizuna AI gère votre compte et la facturation. Avec le contexte d’écran activé (moteur Qwen), les images de la fenêtre, de l’écran ou de l’onglet de la réunion utilisé suivent le même chemin ; Kizuna AI ne les reçoit jamais.',
      },
      {
        question: 'Soniox ou Qwen : quel moteur choisir ?',
        answer:
          'Soniox est le moteur polyvalent : 60 langues, environ 200 voix prédéfinies ou une voix clonée à partir d’un enregistrement, une session partagée pour le mode Les deux, la conversation en face à face, et des régions aux États-Unis, dans l’Union européenne et au Japon. Qwen, d’Alibaba Cloud, interprète la parole en parole dans un seul modèle, parle 29 de ses 60 langues, peut cloner votre voix en direct pendant que vous parlez sans rien enregistrer, et fonctionne à Singapour ou à Pékin. Les deux se paient sur le même solde, à leurs propres tarifs.',
      },
      {
        question: 'Le moteur Qwen peut-il voir mes diapositives ?',
        answer:
          'Oui : activez Contexte d’écran dans les paramètres du moteur Qwen ; dans l’application de bureau, vous choisissez la fenêtre ou l’écran au moment de l’activer, et l’extension de navigateur utilise l’onglet de la réunion. Tant qu’il est activé, il s’ajoute au coût de chaque seconde de parole ; le guide de configuration de Qwen l’explique.',
      },
      {
        question: 'Comment relancer le guide de configuration ?',
        answer: 'Ouvrez les Paramètres, faites défiler jusqu’à Aide et cliquez sur Relancer la configuration.',
      },
    ],
  },
  troubleshooting: {
    title: 'Dépannage',
    content:
      'Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ar: TutorialData = {
  pageTitle: 'البدء السريع مع KizunaAI',
  backLink: 'العودة إلى مزودي الذكاء الاصطناعي',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: 'نظرة عامة',
    content:
      'KizunaAI هي أسرع طريقة للبدء مع Sokuji: لا حاجة إلى إنشاء مفتاح API. تسجّل الدخول بحساب Kizuna AI، وتعمل الترجمة على أحد محركين هما Soniox وQwen، تتحمّل Kizuna AI تكلفتهما وتحاسبك عليها من رصيدك. تحصل الحسابات التي تم التحقق من بريدها الإلكتروني على رصيد تجريبي (حاليًا US$0.50، صالح لمدة 7 أيام)؛ بعد ذلك تشحن رصيدك وتدفع مقابل ما تستخدمه فقط، دون اشتراك. يرشدك هذا الدليل خلال التشغيل الأول لتطبيق Sokuji لسطح المكتب. لقطات الشاشة مأخوذة من Sokuji 0.44.0 على Windows، والواجهة مماثلة على macOS وLinux.',
  },
  steps: [
    {
      title: 'ثبّت Sokuji',
      content:
        'نزّل تطبيق سطح المكتب من <a href="/download">صفحة التنزيل</a> وثبّته (راجع أدلة <a href="/docs/install/windows">Windows</a> و<a href="/docs/install/macos">macOS</a> و<a href="/docs/install/linux">Linux</a>). عند التشغيل الأول، يفتح Sokuji دليل إعداد من ست خطوات.',
      tip: 'على Windows، يطلب Sokuji عند بدء تشغيله تثبيت VB-CABLE، وهو برنامج تشغيل صوت افتراضي مجاني. انقر على Install Now إذا كنت تريد أن يسمع الطرف الآخر في المكالمة صوتك المترجَم.',
    },
    {
      title: 'اختر لغة الواجهة',
      content:
        'هذه هي لغة قوائم Sokuji وأزراره فقط. أما اللغات التي تترجم بينها فتختارها لاحقًا. انقر على <strong>التالي</strong>.',
      screenshot: '01-language.png',
    },
    {
      title: 'أخبر Sokuji بما تريد فعله',
      content:
        'اختر البطاقة الأقرب إلى طريقة استخدامك لـ Sokuji. فهي تحدد وضع الترجمة (<strong>أنا</strong> أو <strong>الآخر</strong> أو <strong>الكل</strong>) وما إذا كانت الترجمة تُنطق بصوت أو تُعرض كترجمة نصية فقط. يمنحك خيار <strong>افهم ما يقوله الآخرون</strong> ترجمة فورية لما تسمعه؛ ويتيح خيار <strong>كي تُفهَم في اجتماع</strong> للطرف الآخر سماع صوتك المترجَم؛ أما <strong>محادثة عبر الإنترنت بالاتجاهين</strong> فيفعل الأمرين معًا، وبمجرد أن يستمع Sokuji إلى تطبيق الاجتماع لديك، يقرأ عليك أيضًا ترجمة الطرف الآخر بصوت مسموع. وبطاقتا <strong>محادثة وجهًا لوجه</strong> مخصّصتان لشخصين أمام حاسوب واحد. يمكنك تغيير كل ذلك لاحقًا.',
      screenshot: '02-scenario.png',
      tip: 'لإجراء مكالمة فيديو مع شخص يتحدث لغة أخرى، اختر «محادثة عبر الإنترنت بالاتجاهين»، ثم اتبع دليل الاجتماع ثنائي الاتجاه. ولشخص يجلس بجانبك، اختر «محادثة وجهًا لوجه» واتبع دليل المحادثة وجهًا لوجه.',
    },
    {
      title: 'اختر «ابدأ فوراً»',
      content:
        'اختر <strong>ابدأ فوراً</strong>. عندها يُشغّل Kizuna AI الترجمة نيابة عنك، فلا حاجة إلى إنشاء مفتاح API أو لصقه؛ ويختار الإعداد محرك Soniox، ويمكنك التبديل إلى Qwen لاحقًا (انظر أدناه). انقر على <strong>التالي</strong>.',
      screenshot: '03-provider.png',
    },
    {
      title: 'سجّل الدخول أو أنشئ حسابًا',
      content:
        'انقر على <strong>إنشاء حساب</strong> إذا كنت جديدًا على Kizuna AI، أو على <strong>تسجيل الدخول</strong> إذا كان لديك حساب بالفعل.',
      screenshot: '04-account.png',
    },
    {
      title: 'املأ نموذج التسجيل',
      content:
        'أدخل اسمك وعنوان بريدك الإلكتروني وكلمة مرور لا تقل عن 8 أحرف، ثم انقر على <strong>إنشاء حساب</strong>. الحساب نفسه يتيح لك أيضًا تسجيل الدخول إلى موقع Kizuna AI، حيث تدير رصيدك.',
      screenshot: '05-create-account.png',
    },
    {
      title: 'تحقّق من بريدك الإلكتروني',
      content:
        'يسجّل Sokuji دخولك فورًا ويرسل إليك رابط تحقق بالبريد الإلكتروني. افتح هذا الرابط: يُضاف الرصيد التجريبي إلى رصيدك بمجرد التحقق من عنوانك. ثم انقر على <strong>التالي</strong>.',
      screenshot: '06-verify-email.png',
      tip: 'لم تصلك رسالة؟ تحقّق من مجلد الرسائل غير المرغوب فيها. يمكنك إكمال الإعداد في هذه الأثناء؛ يظهر الرصيد على زر الحساب بمجرد التحقق.',
    },
    {
      title: 'اختر لغاتك',
      content:
        '<strong>أتحدث</strong> هي لغتك؛ و<strong>يسمعون</strong> هي اللغة التي تصل إلى الطرف الآخر. في الوضع ثنائي الاتجاه يُضبط الاتجاه المعاكس تلقائيًا، ويعرضه السطر الموجود أسفله: هنا «يتحدثون اليابانية ← أقرأ الإنجليزية». ويقول السطر «أسمع» بدلًا من ذلك عندما ستُقرأ عليك ترجمة الطرف الآخر بصوت مسموع.',
      screenshot: '07-languages.png',
      tip: 'إذا كان كلام الطرف الآخر يُترجَم أيضًا (وضع الآخر أو الكل)، فاضبط «أتحدث» على لغة محددة بدلًا من «اكتشاف تلقائي». وإلا فلن يعرف Sokuji اللغة التي يترجم إليها كلامهم، ويبقى زر «ابدأ» معطّلًا.',
    },
    {
      title: 'راجع وأنهِ',
      content:
        'راجع الملخص وانقر على <strong>إنهاء</strong>. تليه جولة قصيرة في النافذة الرئيسية. يمكنك تخطيها وإعادة تشغيلها في أي وقت من <strong>الإعدادات ← مساعدة</strong>.',
      screenshot: '08-ready.png',
    },
    {
      title: 'اختر الميكروفون وابدأ',
      content:
        'تعرض لوحة الإعدادات على اليمين ما اختاره الإعداد. تحت <strong>ميكروفون</strong>، اختر الميكروفون الذي تتحدث فيه. ثم انقر على <strong>ابدأ</strong> أسفل النافذة. إذا بقي زر «ابدأ» رماديًا، فمرّر المؤشر فوقه: تخبرك الملاحظة بما لا يزال ناقصًا.',
      screenshot: '09-main-window.png',
      tip: 'يحتاج وضع الكل أيضًا إلى ضبط «صوت الآخر»، حتى يعرف Sokuji التطبيق الذي يأتي منه صوت الطرف الآخر. يشرح دليل الاجتماع ثنائي الاتجاه ذلك. أما المحادثة وجهًا لوجه فلا تحتاج إليه: الشخص الآخر يشاركك الميكروفون.',
    },
    {
      title: 'تحقّق من رصيدك واشحنه',
      content:
        'يعرض زر الحساب في شريط العنوان رصيدك. انقر عليه لرؤية عنوان بريدك الإلكتروني ورصيدك وما أنفقته خلال آخر 30 يومًا. يفتح <strong>شحن الرصيد</strong> صفحة الفوترة في حساب Kizuna AI الخاص بك في المتصفح، حيث تضيف الرصيد ويمكنك الاطلاع على الأسعار الحالية قبل الدفع.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'بدّل المحرك (اختياري)',
      content:
        'يعمل KizunaAI على أحد محركين، وتعرض قائمة مزوّد خدمة الذكاء الاصطناعي كليهما: <strong>KizunaAI — مدعوم من Soniox</strong>، وهو الخيار الافتراضي الموصى به الذي اختاره الإعداد، و<strong>KizunaAI — مدعوم من Qwen</strong>. للتبديل، افتح <strong>الإعدادات</strong>، وانتقل إلى <strong>متقدم</strong>، ثم اختر الخيار الآخر ضمن <strong>المزود</strong>؛ يُطبَّق الاختيار بدءًا من الجلسة التالية. يشرح قسم الأسئلة الشائعة أدناه الفرق بين المحركين.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: 'نصائح لأفضل النتائج',
    items: [
      'استخدم سماعات الرأس. بدونها قد يلتقط الميكروفون الصوت المترجَم ويترجمه مرة أخرى.',
      'عندما تحتاج إلى الترجمة النصية فقط، أوقف «الترجمة التي يسمعها الطرف الآخر» في قسم «الكلام» في الإعدادات، واترك «الترجمة التي أسمعها» متوقفة. توليد الكلام يكلّف أكثر.',
      'أوقف الجلسة عند الانتهاء: الجلسة الجارية تواصل استهلاك رصيدك.',
      'تعني النقطة الحمراء على زر الحساب أن الرصيد منخفض جدًا لبدء جلسة؛ وتعني النقطة الكهرمانية أن عنوان بريدك الإلكتروني لم يتم التحقق منه بعد.',
    ],
  },
  faq: {
    title: 'الأسئلة الشائعة',
    items: [
      {
        question: 'كم التكلفة؟',
        answer:
          'تُدفع الترجمة من رصيد مسبق الدفع، حسب الاستخدام، دون اشتراك. يعتمد السعر على المحرك وعلى ما تفعله الجلسة (على سبيل المثال، الإخراج الصوتي يكلّف أكثر من الترجمة النصية فقط). تعرض صفحة الفوترة في حساب Kizuna AI الأسعار الحالية قبل أن تشحن رصيدك.',
      },
      {
        question: 'ماذا يحدث عندما ينفد رصيدي؟',
        answer:
          'لا يبدأ Sokuji جلسة ما دام الرصيد منخفضًا جدًا، وتتوقف الجلسة الجارية عند نفاد رصيدها. اشحن رصيدك، ثم انقر على «ابدأ» مرة أخرى.',
      },
      {
        question: 'إلى أين يذهب صوتي؟',
        answer:
          'يرسل Sokuji صوتك مباشرة إلى المحرك الذي اخترته — Soniox، أو خدمة Qwen من Alibaba Cloud في المنطقة التي اخترتها — باستخدام مفتاح قصير الأجل صادر لحسابك. ويتولى Kizuna AI حسابك والفوترة. وعند تفعيل سياق الشاشة (محرك Qwen)، تسلك صور النافذة أو الشاشة أو علامة تبويب الاجتماع المستخدمة الطريق نفسه؛ ولا يستلمها Kizuna AI أبدًا.',
      },
      {
        question: 'Soniox أم Qwen: أي محرك أختار؟',
        answer:
          'Soniox هو الخيار الشامل: 60 لغة، ونحو 200 صوت جاهز أو صوت مستنسخ من تسجيل، وجلسة مشتركة لوضع الكل، والمحادثة وجهًا لوجه، ومناطق في الولايات المتحدة والاتحاد الأوروبي واليابان. أما Qwen من Alibaba Cloud فيترجم الكلام إلى كلام في نموذج واحد، وينطق 29 لغة من لغاته الـ 60، ويمكنه استنساخ صوتك مباشرةً أثناء حديثك دون الحاجة إلى تسجيل أي شيء، ويعمل في سنغافورة أو بكين. ويُدفع مقابل كليهما من الرصيد نفسه، بسعر خاص بكل منهما.',
      },
      {
        question: 'هل يستطيع محرك Qwen رؤية شرائحي؟',
        answer:
          'نعم: فعّل «سياق الشاشة» في إعدادات محرك Qwen؛ ففي تطبيق سطح المكتب تختار النافذة أو الشاشة عند تفعيله، وتستخدم إضافة المتصفح علامة تبويب الاجتماع. ويضيف ذلك إلى الرسوم عن كل ثانية كلام ما دام مفعّلًا؛ ويشرح دليل إعداد Qwen ذلك.',
      },
      {
        question: 'كيف أشغّل دليل الإعداد مرة أخرى؟',
        answer: 'افتح الإعدادات، ومرّر إلى «مساعدة» وانقر على «تشغيل الإعداد مرة أخرى».',
      },
    ],
  },
  troubleshooting: {
    title: 'استكشاف الأخطاء وإصلاحها',
    content:
      'هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const pt: TutorialData = {
  pageTitle: 'Início rápido com KizunaAI',
  backLink: 'Voltar aos provedores de IA',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: 'Visão geral',
    content:
      'A KizunaAI é o jeito mais rápido de começar com o Sokuji: não é preciso criar nenhuma chave de API. Você entra com uma conta Kizuna AI, e a tradução roda em um de dois mecanismos, a Soniox ou a Qwen, que a Kizuna AI paga e debita do seu saldo. Contas com endereço de e-mail verificado recebem um crédito de teste (atualmente US$ 0,50, válido por 7 dias); depois disso, você recarrega seu saldo e paga só pelo que usar, sem assinatura. Este guia mostra a primeira abertura do aplicativo de desktop do Sokuji. As capturas de tela são do Sokuji 0.44.0 no Windows; no macOS e no Linux a aparência é a mesma.',
  },
  steps: [
    {
      title: 'Instale o Sokuji',
      content:
        'Baixe o aplicativo de desktop na <a href="/download">página de download</a> e instale-o (veja os guias para <a href="/docs/install/windows">Windows</a>, <a href="/docs/install/macos">macOS</a> e <a href="/docs/install/linux">Linux</a>). Na primeira abertura, o Sokuji mostra um guia de configuração em seis etapas.',
      tip: 'No Windows, o Sokuji oferece instalar o VB-CABLE, um driver de áudio virtual gratuito, ao iniciar. Clique em Install Now se quiser que a outra pessoa da chamada ouça sua voz traduzida.',
    },
    {
      title: 'Escolha o idioma da interface',
      content:
        'Este é apenas o idioma dos menus e botões do Sokuji. Os idiomas entre os quais traduzir você escolhe mais adiante. Clique em <strong>Próximo</strong>.',
      screenshot: '01-language.png',
    },
    {
      title: 'Diga ao Sokuji o que você quer fazer',
      content:
        'Escolha o cartão mais próximo de como você vai usar o Sokuji. Ele define o modo de tradução (<strong>Eu</strong>, <strong>Outro</strong> ou <strong>Ambos</strong>) e se as traduções são faladas ou mostradas apenas como legendas. <strong>Entender o que os outros dizem</strong> dá a você uma tradução ao vivo do que você ouve; <strong>Ser entendido em uma reunião</strong> faz a outra pessoa ouvir sua voz traduzida; <strong>Conversa on-line em dois sentidos</strong> faz as duas coisas ao mesmo tempo e, quando o Sokuji escuta o seu aplicativo de reunião, também lê para você em voz alta a tradução da outra pessoa. Os dois cartões <strong>Conversa cara a cara</strong> são para duas pessoas em um mesmo computador. Você pode mudar tudo isso depois.',
      screenshot: '02-scenario.png',
      tip: 'Para uma videochamada com alguém que fala outro idioma, escolha Conversa on-line em dois sentidos e depois siga o guia de reuniões bidirecionais. Para alguém sentado ao seu lado, escolha Conversa cara a cara e siga o guia de conversa cara a cara.',
    },
    {
      title: 'Escolha “Comece agora”',
      content:
        'Escolha <strong>Comece agora</strong>. A Kizuna AI então faz a tradução para você, de modo que não há chave de API para criar nem colar; a configuração escolhe o mecanismo Soniox, e você pode mudar para a Qwen depois (veja abaixo). Clique em <strong>Próximo</strong>.',
      screenshot: '03-provider.png',
    },
    {
      title: 'Entre ou crie uma conta',
      content:
        'Clique em <strong>Cadastrar</strong> se você é novo na Kizuna AI, ou em <strong>Entrar</strong> se já tem uma conta.',
      screenshot: '04-account.png',
    },
    {
      title: 'Preencha o formulário de cadastro',
      content:
        'Informe seu nome, seu endereço de e-mail e uma senha com pelo menos 8 caracteres e clique em <strong>Cadastrar</strong>. A mesma conta também dá acesso ao site da Kizuna AI, onde você gerencia seu saldo.',
      screenshot: '05-create-account.png',
    },
    {
      title: 'Verifique seu endereço de e-mail',
      content:
        'O Sokuji faz seu login na hora e envia um link de verificação para o seu e-mail. Abra esse link: o crédito de teste é adicionado ao seu saldo assim que o endereço é verificado. Depois clique em <strong>Próximo</strong>.',
      screenshot: '06-verify-email.png',
      tip: 'Não recebeu o e-mail? Confira a pasta de spam. Enquanto isso, você pode terminar a configuração; o crédito aparece no botão da conta assim que você verificar.',
    },
    {
      title: 'Escolha seus idiomas',
      content:
        '<strong>eu falo</strong> é o seu idioma; <strong>eles ouvem</strong> é o idioma que a outra pessoa recebe. No modo bidirecional, a direção inversa é definida para você, e a linha abaixo a mostra: aqui, “Eles falam Japonês → eu leio Inglês”. Em vez disso, ela diz “eu ouço” quando a tradução da outra pessoa for lida em voz alta para você.',
      screenshot: '07-languages.png',
      tip: 'Se a fala da outra pessoa também for traduzida (modo Outro ou Ambos), defina eu falo como um idioma específico em vez de Detecção Automática. Caso contrário, o Sokuji não sabe para qual idioma traduzi-la, e Iniciar continua desativado.',
    },
    {
      title: 'Revise e conclua',
      content:
        'Confira o resumo e clique em <strong>Concluir</strong>. Em seguida vem um breve tour pela janela principal. Você pode pulá-lo e revê-lo quando quiser em <strong>Configurações → Ajuda</strong>.',
      screenshot: '08-ready.png',
    },
    {
      title: 'Escolha seu microfone e comece',
      content:
        'O painel Configurações à direita mostra o que a configuração escolheu. Em <strong>Microfone</strong>, escolha o microfone em que você fala. Depois clique em <strong>Iniciar</strong>, na parte de baixo da janela. Se Iniciar continuar cinza, passe o mouse sobre ele: a nota diz o que ainda está faltando.',
      screenshot: '09-main-window.png',
      tip: 'O modo Ambos também exige que Áudio do outro esteja definido, para que o Sokuji saiba de qual aplicativo a outra pessoa está falando. O guia de reuniões bidirecionais explica isso. A conversa cara a cara não precisa disso: a outra pessoa compartilha o seu microfone.',
    },
    {
      title: 'Confira seu saldo e recarregue',
      content:
        'O botão da conta, na barra de título, mostra seu saldo. Clique nele para ver seu endereço de e-mail, seu saldo e quanto você gastou nos últimos 30 dias. <strong>Recarregar</strong> abre no navegador a página de cobrança da sua conta Kizuna AI, onde você adiciona fundos e pode ver as tarifas atuais antes de pagar.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'Troque o mecanismo (opcional)',
      content:
        'A KizunaAI roda em um de dois mecanismos, e a lista de provedores de serviços de IA mostra os dois: <strong>KizunaAI — Com tecnologia Soniox</strong>, o padrão recomendado, que a configuração escolheu, e <strong>KizunaAI — Com tecnologia Qwen</strong>. Para trocar, abra <strong>Configurações</strong>, mude para <strong>Avançado</strong> e escolha o outro em <strong>Provedor</strong>; a escolha vale a partir da próxima sessão. As perguntas frequentes abaixo explicam como os dois diferem.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: 'Dicas para melhores resultados',
    items: [
      'Use fones de ouvido. Sem eles, seu microfone pode captar a voz traduzida e traduzi-la de novo.',
      'Quando precisar só de legendas, desative Tradução que o outro lado ouve na seção Voz das Configurações e deixe Tradução que eu ouço desativada. Gerar fala custa mais.',
      'Pare a sessão quando terminar: uma sessão em andamento continua consumindo seu saldo.',
      'Um ponto vermelho no botão da conta significa que o saldo está baixo demais para iniciar uma sessão; um ponto âmbar significa que seu endereço de e-mail ainda não foi verificado.',
    ],
  },
  faq: {
    title: 'Perguntas frequentes',
    items: [
      {
        question: 'Quanto custa?',
        answer:
          'A tradução é paga com um saldo pré-pago, por uso, sem assinatura. A tarifa depende do mecanismo e do que a sessão faz (por exemplo, a saída falada custa mais do que só legendas). A página de cobrança da sua conta Kizuna AI mostra as tarifas atuais antes de você recarregar.',
      },
      {
        question: 'O que acontece quando meu saldo acaba?',
        answer:
          'O Sokuji não inicia uma sessão enquanto o saldo estiver baixo demais, e uma sessão em andamento para quando o saldo dela acaba. Recarregue e clique em Iniciar de novo.',
      },
      {
        question: 'Para onde vai o meu áudio?',
        answer:
          'O Sokuji envia seu áudio direto para o mecanismo que você escolheu (a Soniox ou o serviço Qwen da Alibaba Cloud, na região selecionada), usando uma chave de curta duração emitida para a sua conta. A Kizuna AI cuida da sua conta e da cobrança. Com o contexto da tela ativado (mecanismo Qwen), as imagens da janela, tela ou aba da reunião em uso seguem o mesmo caminho; a Kizuna AI nunca as recebe.',
      },
      {
        question: 'Soniox ou Qwen: qual mecanismo devo escolher?',
        answer:
          'A Soniox é a opção mais versátil: 60 idiomas, cerca de 200 vozes predefinidas ou uma voz clonada a partir de uma gravação, uma sessão compartilhada para o modo Ambos, conversa cara a cara e regiões nos Estados Unidos, na União Europeia e no Japão. A Qwen, da Alibaba Cloud, interpreta de fala para fala em um único modelo, fala 29 dos seus 60 idiomas, pode clonar a sua voz ao vivo enquanto você fala, sem nada para gravar, e roda em Singapura ou em Pequim. As duas são pagas com o mesmo saldo, cada uma com as suas tarifas.',
      },
      {
        question: 'O mecanismo Qwen consegue ver meus slides?',
        answer:
          'Sim: ative Contexto da tela nas configurações do mecanismo Qwen; no app para desktop, você escolhe a janela ou a tela quando o ativa, e a extensão do navegador usa a aba da reunião. Enquanto estiver ativado, isso aumenta a cobrança por cada segundo de fala; o guia de configuração da Qwen explica como funciona.',
      },
      {
        question: 'Como abro o guia de configuração de novo?',
        answer: 'Abra Configurações, role até Ajuda e clique em Refazer a configuração.',
      },
    ],
  },
  troubleshooting: {
    title: 'Solução de problemas',
    content:
      'Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Você também pode perguntar no <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const ru: TutorialData = {
  pageTitle: 'Быстрый старт с KizunaAI',
  backLink: 'Назад к провайдерам ИИ',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: 'Обзор',
    content:
      'KizunaAI — самый быстрый способ начать работу с Sokuji: создавать API-ключ не нужно. Вы входите с учётной записью Kizuna AI, а перевод выполняет один из двух движков, Soniox или Qwen: Kizuna AI оплачивает его и списывает стоимость с вашего баланса. Учётные записи с подтверждённым адресом электронной почты получают пробный кредит (сейчас US$0.50, действует 7 дней); после этого вы пополняете баланс и платите только за то, что используете, без подписки. Это руководство описывает первый запуск настольного приложения Sokuji. Снимки экрана сделаны в Sokuji 0.44.0 на Windows; на macOS и Linux всё выглядит так же.',
  },
  steps: [
    {
      title: 'Установите Sokuji',
      content:
        'Скачайте настольное приложение со <a href="/download">страницы загрузки</a> и установите его (см. руководства для <a href="/docs/install/windows">Windows</a>, <a href="/docs/install/macos">macOS</a> и <a href="/docs/install/linux">Linux</a>). При первом запуске Sokuji открывает мастер настройки из шести шагов.',
      tip: 'На Windows при запуске Sokuji предлагает установить VB-CABLE — бесплатный виртуальный аудиодрайвер. Нажмите Install Now, если хотите, чтобы собеседник во время звонка слышал ваш переведённый голос.',
    },
    {
      title: 'Выберите язык интерфейса',
      content:
        'Это только язык меню и кнопок Sokuji. Языки для перевода вы выберете позже. Нажмите <strong>Далее</strong>.',
      screenshot: '01-language.png',
    },
    {
      title: 'Расскажите Sokuji, что вы хотите сделать',
      content:
        'Выберите карточку, которая лучше всего соответствует тому, как вы будете использовать Sokuji. От неё зависят режим перевода (<strong>Я</strong>, <strong>Собеседник</strong> или <strong>Оба</strong>) и то, будет ли перевод озвучиваться или показываться только субтитрами. <strong>Понимать, что говорят другие</strong> даёт вам живой перевод того, что вы слышите; <strong>Чтобы вас понимали на встрече</strong> позволяет собеседнику слышать ваш переведённый голос; <strong>Двусторонний онлайн-разговор</strong> делает и то и другое одновременно, а когда Sokuji слушает ваше приложение для встреч, он ещё и зачитывает вам вслух перевод собеседника. Две карточки <strong>Разговор лицом к лицу</strong> предназначены для двух человек за одним компьютером. Всё это можно изменить позже.',
      screenshot: '02-scenario.png',
      tip: 'Для видеозвонка с человеком, который говорит на другом языке, выберите «Двусторонний онлайн-разговор», а затем следуйте руководству по двусторонней встрече. Если собеседник сидит рядом с вами, выберите «Разговор лицом к лицу» и следуйте руководству по разговору лицом к лицу.',
    },
    {
      title: 'Выберите «Начать сразу»',
      content:
        'Выберите <strong>Начать сразу</strong>. Тогда перевод для вас выполняет Kizuna AI, поэтому создавать и вставлять API-ключ не нужно; мастер выбирает движок Soniox, а перейти на Qwen можно позже (см. ниже). Нажмите <strong>Далее</strong>.',
      screenshot: '03-provider.png',
    },
    {
      title: 'Войдите или создайте учётную запись',
      content:
        'Нажмите <strong>Регистрация</strong>, если вы впервые пользуетесь Kizuna AI, или <strong>Войти</strong>, если у вас уже есть учётная запись.',
      screenshot: '04-account.png',
    },
    {
      title: 'Заполните форму регистрации',
      content:
        'Введите имя, адрес электронной почты и пароль длиной не менее 8 символов, затем нажмите <strong>Регистрация</strong>. С этой же учётной записью вы входите на сайт Kizuna AI, где управляете балансом.',
      screenshot: '05-create-account.png',
    },
    {
      title: 'Подтвердите адрес электронной почты',
      content:
        'Sokuji сразу выполняет вход и отправляет вам письмо со ссылкой для подтверждения. Откройте эту ссылку: пробный кредит зачисляется на баланс после подтверждения адреса. Затем нажмите <strong>Далее</strong>.',
      screenshot: '06-verify-email.png',
      tip: 'Письма нет? Проверьте папку «Спам». Тем временем можно завершить настройку: кредит появится на кнопке учётной записи, как только вы подтвердите адрес.',
    },
    {
      title: 'Выберите языки',
      content:
        'Поле <strong>я говорю на</strong> — ваш язык; <strong>собеседник слышит</strong> — язык, который получает другая сторона. В двустороннем режиме обратное направление задаётся автоматически, и строка под полями показывает его: здесь «Собеседник говорит на Японский → я читаю Английский». Когда перевод собеседника будет зачитываться вам вслух, там вместо этого написано «я слышу».',
      screenshot: '07-languages.png',
      tip: 'Если речь собеседника тоже переводится (режим «Собеседник» или «Оба»), укажите в поле «я говорю на» конкретный язык, а не «Автоопределение». Иначе Sokuji не знает, на какой язык переводить собеседника, и кнопка «Старт» остаётся недоступной.',
    },
    {
      title: 'Проверьте и завершите',
      content:
        'Проверьте сводку и нажмите <strong>Готово</strong>. Затем начнётся короткий обзор главного окна. Его можно пропустить и в любой момент запустить снова в разделе <strong>Настройки → Помощь</strong>.',
      screenshot: '08-ready.png',
    },
    {
      title: 'Выберите микрофон и начните',
      content:
        'Панель настроек справа показывает, что было выбрано при настройке. В разделе <strong>Микрофон</strong> выберите микрофон, в который вы говорите. Затем нажмите <strong>Старт</strong> внизу окна. Если кнопка «Старт» остаётся серой, наведите на неё курсор: подсказка сообщит, чего ещё не хватает.',
      screenshot: '09-main-window.png',
      tip: 'В режиме «Оба» также нужно указать «Аудио собеседника», чтобы Sokuji знал, из какого приложения говорит другая сторона. Об этом рассказывает руководство по двусторонней встрече. Для разговора лицом к лицу это не нужно: собеседник использует ваш микрофон.',
    },
    {
      title: 'Проверьте баланс и пополните его',
      content:
        'Кнопка учётной записи в строке заголовка показывает ваш баланс. Нажмите её, чтобы увидеть свой адрес электронной почты, баланс и расходы за последние 30 дней. <strong>Пополнить</strong> открывает в браузере страницу оплаты вашей учётной записи Kizuna AI: там вы вносите средства и перед оплатой видите текущие тарифы.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'Переключите движок (по желанию)',
      content:
        'KizunaAI работает на одном из двух движков, и в списке поставщиков услуг ИИ есть оба: <strong>KizunaAI — Работает на Soniox</strong>, рекомендуемый вариант по умолчанию, который выбрал мастер, и <strong>KizunaAI — Работает на Qwen</strong>. Чтобы сменить движок, откройте <strong>Настройки</strong>, переключитесь в режим <strong>Расширенный</strong> и выберите другой вариант на вкладке <strong>Поставщик</strong>; выбор действует со следующего сеанса. Чем два движка отличаются, объясняется в вопросах ниже.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: 'Советы для лучших результатов',
    items: [
      'Используйте наушники. Без них микрофон может уловить переведённый голос и перевести его ещё раз.',
      'Если вам нужны только субтитры, выключите «Перевод, который слышит собеседник» в блоке «Речь» в настройках и оставьте выключенным «Перевод, который слышу я». Синтез речи стоит дополнительно.',
      'Останавливайте сеанс, когда закончите: запущенный сеанс продолжает расходовать баланс.',
      'Красная точка на кнопке учётной записи означает, что баланса недостаточно для начала сеанса; жёлтая — что адрес электронной почты ещё не подтверждён.',
    ],
  },
  faq: {
    title: 'Часто задаваемые вопросы',
    items: [
      {
        question: 'Сколько это стоит?',
        answer:
          'Перевод оплачивается с предоплаченного баланса по факту использования, без подписки. Тариф зависит от движка и от того, что делает сеанс (например, озвучивание стоит дороже, чем только субтитры). На странице оплаты вашей учётной записи Kizuna AI указаны текущие тарифы — до пополнения.',
      },
      {
        question: 'Что произойдёт, когда баланс закончится?',
        answer:
          'Sokuji не начинает сеанс, пока баланса недостаточно, а запущенный сеанс останавливается, когда его баланс исчерпан. Пополните баланс и снова нажмите «Старт».',
      },
      {
        question: 'Куда передаётся мой звук?',
        answer:
          'Sokuji отправляет ваш звук напрямую выбранному вами движку — Soniox или сервису Qwen компании Alibaba Cloud в выбранном вами регионе — используя краткосрочный ключ, выданный для вашей учётной записи. Kizuna AI отвечает за вашу учётную запись и оплату. Если включён контекст экрана (движок Qwen), изображения используемого окна, экрана или вкладки встречи идут тем же путём; Kizuna AI их никогда не получает.',
      },
      {
        question: 'Soniox или Qwen: какой движок выбрать?',
        answer:
          'Soniox — универсальный вариант: 60 языков, около 200 готовых голосов или голос, клонированный по записи, общий сеанс для режима «Оба», разговор лицом к лицу и регионы в США, Европейском союзе и Японии. Qwen от Alibaba Cloud переводит речь в речь одной моделью, озвучивает 29 из своих 60 языков, может клонировать ваш голос вживую во время разговора, не требуя никакой записи, и работает в Сингапуре или Пекине. Оба движка оплачиваются с одного баланса, каждый по своему тарифу.',
      },
      {
        question: 'Может ли движок Qwen видеть мои слайды?',
        answer:
          'Да: включите «Контекст экрана» в настройках движка Qwen; в настольном приложении окно или экран выбирается при включении, а расширение для браузера использует вкладку встречи. Пока он включён, плата за каждую секунду речи увеличивается; подробности — в руководстве по настройке Qwen.',
      },
      {
        question: 'Как снова запустить мастер настройки?',
        answer: 'Откройте «Настройки», прокрутите до раздела «Помощь» и нажмите «Запустить настройку снова».',
      },
    ],
  },
  troubleshooting: {
    title: 'Устранение неполадок',
    content:
      'Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Также можно задать вопрос в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const de: TutorialData = {
  pageTitle: 'KizunaAI-Schnellstart',
  backLink: 'Zurück zu KI-Anbietern',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: 'Übersicht',
    content:
      'KizunaAI ist der schnellste Weg, mit Sokuji loszulegen: Sie müssen keinen API-Schlüssel erstellen. Sie melden sich mit einem Kizuna AI-Konto an, und die Übersetzung läuft auf einer von zwei Engines, Soniox oder Qwen, die Kizuna AI für Sie bezahlt und über Ihr Guthaben abrechnet. Konten mit bestätigter E-Mail-Adresse erhalten ein Testguthaben (derzeit US$0.50, 7 Tage gültig); danach laden Sie Ihr Guthaben auf und zahlen nur für das, was Sie nutzen, ohne Abonnement. Diese Anleitung führt durch den ersten Start der Sokuji-Desktop-App. Die Screenshots stammen aus Sokuji 0.44.0 unter Windows; unter macOS und Linux sieht es genauso aus.',
  },
  steps: [
    {
      title: 'Sokuji installieren',
      content:
        'Laden Sie die Desktop-App von der <a href="/download">Download-Seite</a> herunter und installieren Sie sie (siehe die Anleitungen für <a href="/docs/install/windows">Windows</a>, <a href="/docs/install/macos">macOS</a> und <a href="/docs/install/linux">Linux</a>). Beim ersten Start öffnet Sokuji eine Einrichtung in sechs Schritten.',
      tip: 'Unter Windows fragt Sokuji beim Start, ob VB-CABLE installiert werden soll, ein kostenloser virtueller Audiotreiber. Klicken Sie auf Install Now, wenn Ihr Gesprächspartner im Anruf Ihre übersetzte Stimme hören soll.',
    },
    {
      title: 'Sprache der Benutzeroberfläche wählen',
      content:
        'Das ist nur die Sprache der Menüs und Schaltflächen von Sokuji. Die Sprachen, zwischen denen übersetzt wird, wählen Sie später. Klicken Sie auf <strong>Weiter</strong>.',
      screenshot: '01-language.png',
    },
    {
      title: 'Sokuji sagen, was Sie vorhaben',
      content:
        'Wählen Sie die Karte, die am besten dazu passt, wie Sie Sokuji nutzen werden. Sie legt den Übersetzungsmodus fest (<strong>Ich</strong>, <strong>Gegenüber</strong> oder <strong>Beides</strong>) und ob Übersetzungen gesprochen oder nur als Untertitel angezeigt werden. <strong>Verstehen, was andere sagen</strong> übersetzt live, was Sie hören; mit <strong>In einem Meeting verstanden werden</strong> hört die andere Seite Ihre übersetzte Stimme; <strong>Zweiseitiges Online-Gespräch</strong> macht beides gleichzeitig, und sobald Sokuji Ihrer Meeting-App zuhört, liest es Ihnen auch die Übersetzung der anderen Seite vor. Die beiden Karten <strong>Gespräch von Angesicht zu Angesicht</strong> sind für zwei Personen an einem Computer gedacht. Sie können all das später ändern.',
      screenshot: '02-scenario.png',
      tip: 'Für einen Videoanruf mit jemandem, der eine andere Sprache spricht, wählen Sie „Zweiseitiges Online-Gespräch“ und folgen dann der Anleitung für zweiseitige Meetings. Für jemanden, der neben Ihnen sitzt, wählen Sie „Gespräch von Angesicht zu Angesicht“ und folgen der Anleitung für Gespräche von Angesicht zu Angesicht.',
    },
    {
      title: '„Sofort loslegen“ wählen',
      content:
        'Wählen Sie <strong>Sofort loslegen</strong>. Kizuna AI übernimmt dann die Übersetzung für Sie, sodass Sie keinen API-Schlüssel erstellen oder einfügen müssen; die Einrichtung wählt die Soniox-Engine, und Sie können später zu Qwen wechseln (siehe unten). Klicken Sie auf <strong>Weiter</strong>.',
      screenshot: '03-provider.png',
    },
    {
      title: 'Anmelden oder Konto erstellen',
      content:
        'Klicken Sie auf <strong>Konto erstellen</strong>, wenn Sie neu bei Kizuna AI sind, oder auf <strong>Anmelden</strong>, wenn Sie bereits ein Konto haben.',
      screenshot: '04-account.png',
    },
    {
      title: 'Registrierungsformular ausfüllen',
      content:
        'Geben Sie Ihren Namen, Ihre E-Mail-Adresse und ein Passwort mit mindestens 8 Zeichen ein und klicken Sie dann auf <strong>Registrieren</strong>. Mit demselben Konto melden Sie sich auch auf der Kizuna AI-Website an, wo Sie Ihr Guthaben verwalten.',
      screenshot: '05-create-account.png',
    },
    {
      title: 'E-Mail-Adresse bestätigen',
      content:
        'Sokuji meldet Sie sofort an und schickt Ihnen einen Bestätigungslink per E-Mail. Öffnen Sie diesen Link: Das Testguthaben wird Ihrem Konto gutgeschrieben, sobald Ihre Adresse bestätigt ist. Klicken Sie dann auf <strong>Weiter</strong>.',
      screenshot: '06-verify-email.png',
      tip: 'Keine E-Mail erhalten? Sehen Sie im Spam-Ordner nach. Sie können die Einrichtung in der Zwischenzeit abschließen; das Guthaben erscheint auf der Konto-Schaltfläche, sobald Sie die Adresse bestätigen.',
    },
    {
      title: 'Sprachen wählen',
      content:
        '<strong>Ich spreche</strong> ist Ihre Sprache; <strong>sie hören</strong> ist die Sprache, die die andere Seite erhält. Im zweiseitigen Modus wird die Gegenrichtung automatisch eingestellt, und die Zeile darunter zeigt sie an: hier „Sie sprechen Japanisch → ich lese Englisch“. Wird Ihnen die Übersetzung der anderen Seite vorgelesen, steht dort stattdessen „ich höre“.',
      screenshot: '07-languages.png',
      tip: 'Wenn auch die Sprache der anderen Seite übersetzt wird (Modus „Gegenüber“ oder „Beides“), stellen Sie bei „Ich spreche“ eine bestimmte Sprache ein statt „Automatische Erkennung“. Sonst weiß Sokuji nicht, in welche Sprache die andere Seite übersetzt werden soll, und „Starten“ bleibt deaktiviert.',
    },
    {
      title: 'Prüfen und abschließen',
      content:
        'Prüfen Sie die Zusammenfassung und klicken Sie auf <strong>Fertigstellen</strong>. Danach folgt eine kurze Tour durch das Hauptfenster. Sie können sie überspringen und jederzeit unter <strong>Einstellungen → Hilfe</strong> erneut abspielen.',
      screenshot: '08-ready.png',
    },
    {
      title: 'Mikrofon wählen und starten',
      content:
        'Der Einstellungsbereich rechts zeigt, was die Einrichtung gewählt hat. Wählen Sie unter <strong>Mikrofon</strong> das Mikrofon, in das Sie sprechen. Klicken Sie dann unten im Fenster auf <strong>Starten</strong>. Bleibt „Starten“ grau, bewegen Sie den Mauszeiger darüber: Der Hinweis sagt Ihnen, was noch fehlt.',
      screenshot: '09-main-window.png',
      tip: 'Im Modus „Beides“ muss außerdem „Audio des Gegenübers“ eingestellt sein, damit Sokuji weiß, aus welcher App die andere Seite spricht. Das behandelt die Anleitung für zweiseitige Meetings. Bei einem Gespräch von Angesicht zu Angesicht ist das nicht nötig: Die andere Person teilt sich Ihr Mikrofon.',
    },
    {
      title: 'Guthaben prüfen und aufladen',
      content:
        'Die Konto-Schaltfläche in der Titelleiste zeigt Ihr Guthaben. Klicken Sie darauf, um Ihre E-Mail-Adresse, Ihr Guthaben und Ihre Ausgaben der letzten 30 Tage zu sehen. <strong>Aufladen</strong> öffnet die Abrechnungsseite Ihres Kizuna AI-Kontos im Browser; dort laden Sie Guthaben auf und sehen vor dem Bezahlen die aktuellen Preise.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'Engine wechseln (optional)',
      content:
        'KizunaAI läuft auf einer von zwei Engines, und die Liste der KI-Dienstanbieter zeigt beide: <strong>KizunaAI — Unterstützt von Soniox</strong>, die empfohlene Standardeinstellung, die die Einrichtung gewählt hat, und <strong>KizunaAI — Unterstützt von Qwen</strong>. Zum Wechseln öffnen Sie die <strong>Einstellungen</strong>, wechseln zu <strong>Erweitert</strong> und wählen im Tab <strong>Anbieter</strong> den anderen Eintrag; die Auswahl gilt ab der nächsten Sitzung. Die häufig gestellten Fragen unten erklären, worin sich die beiden unterscheiden.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: 'Tipps für beste Ergebnisse',
    items: [
      'Verwenden Sie Kopfhörer. Ohne sie kann Ihr Mikrofon die übersetzte Stimme aufnehmen und erneut übersetzen.',
      'Wenn Sie nur Untertitel brauchen, schalten Sie im Bereich „Sprache“ der Einstellungen „Übersetzung, die das Gegenüber hört“ aus und lassen „Übersetzung, die ich höre“ ausgeschaltet. Die Sprachausgabe kostet extra.',
      'Beenden Sie die Sitzung, wenn Sie fertig sind: Eine laufende Sitzung verbraucht weiter Guthaben.',
      'Ein roter Punkt auf der Konto-Schaltfläche bedeutet, dass das Guthaben zu niedrig ist, um eine Sitzung zu starten; ein gelber Punkt bedeutet, dass Ihre E-Mail-Adresse noch nicht bestätigt ist.',
    ],
  },
  faq: {
    title: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Was kostet das?',
        answer:
          'Die Übersetzung wird nutzungsbasiert aus einem Prepaid-Guthaben bezahlt, ohne Abonnement. Der Preis hängt von der Engine ab und davon, was eine Sitzung tut (zum Beispiel kostet Sprachausgabe mehr als reine Untertitel). Die Abrechnungsseite Ihres Kizuna AI-Kontos zeigt die aktuellen Preise, bevor Sie aufladen.',
      },
      {
        question: 'Was passiert, wenn mein Guthaben aufgebraucht ist?',
        answer:
          'Sokuji startet keine Sitzung, solange das Guthaben zu niedrig ist, und eine laufende Sitzung endet, wenn ihr Guthaben aufgebraucht ist. Laden Sie auf und klicken Sie dann erneut auf „Starten“.',
      },
      {
        question: 'Wohin geht mein Audio?',
        answer:
          'Sokuji sendet Ihr Audio direkt an die Engine, die Sie gewählt haben — Soniox oder den Qwen-Dienst von Alibaba Cloud in der gewählten Region — mit einem kurzlebigen Schlüssel, der für Ihr Konto ausgestellt wird. Kizuna AI kümmert sich um Ihr Konto und die Abrechnung. Mit eingeschaltetem Bildschirmkontext (Qwen-Engine) nehmen Bilder des einen verwendeten Fensters, Bildschirms oder Meeting-Tabs denselben Weg; Kizuna AI erhält sie nie.',
      },
      {
        question: 'Soniox oder Qwen: Welche Engine soll ich wählen?',
        answer:
          'Soniox ist der Allrounder: 60 Sprachen, rund 200 vordefinierte Stimmen oder eine aus einer Aufnahme geklonte Stimme, eine gemeinsame Sitzung für den Modus „Beides“, Gespräche von Angesicht zu Angesicht und Regionen in den USA, der Europäischen Union und Japan. Qwen von Alibaba Cloud dolmetscht in einem einzigen Modell simultan von Sprache zu Sprache, spricht 29 seiner 60 Sprachen, kann Ihre Stimme live beim Sprechen klonen, ohne dass Sie etwas aufnehmen müssen, und läuft in Singapur oder Peking. Beide werden aus demselben Guthaben bezahlt, zu jeweils eigenen Preisen.',
      },
      {
        question: 'Kann die Qwen-Engine meine Folien sehen?',
        answer:
          'Ja: Schalten Sie in den Einstellungen der Qwen-Engine „Bildschirmkontext“ ein; in der Desktop-App wählen Sie beim Einschalten das Fenster oder den Bildschirm, die Browser-Erweiterung verwendet den Meeting-Tab. Solange er eingeschaltet ist, erhöht er die Gebühr für jede Sekunde Sprache; die Qwen-Einrichtungsanleitung erklärt es.',
      },
      {
        question: 'Wie starte ich die Einrichtung erneut?',
        answer: 'Öffnen Sie die Einstellungen, scrollen Sie zu „Hilfe“ und klicken Sie auf „Einrichtung erneut ausführen“.',
      },
    ],
  },
  troubleshooting: {
    title: 'Fehlerbehebung',
    content:
      'Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Sie können auch in den <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a> fragen.',
  },
};

const uk: TutorialData = {
  pageTitle: 'Швидкий старт із KizunaAI',
  backLink: 'Назад до провайдерів ШІ',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: 'Огляд',
    content:
      'KizunaAI — найшвидший спосіб почати роботу з Sokuji: створювати API-ключ не потрібно. Ви входите з обліковим записом Kizuna AI, а переклад виконує один із двох рушіїв, Soniox або Qwen: Kizuna AI сама за нього платить, а вартість списує з вашого балансу. Облікові записи з підтвердженою адресою електронної пошти отримують пробний баланс (зараз US$0.50, діє 7 днів); після цього ви поповнюєте баланс і платите лише за те, що використовуєте, без підписки. Цей посібник описує перший запуск настільного застосунку Sokuji. Знімки екрана зроблено в Sokuji 0.44.0 на Windows; на macOS і Linux усе виглядає так само.',
  },
  steps: [
    {
      title: 'Установіть Sokuji',
      content:
        'Завантажте настільний застосунок зі <a href="/download">сторінки завантаження</a> та встановіть його (див. посібники для <a href="/docs/install/windows">Windows</a>, <a href="/docs/install/macos">macOS</a> і <a href="/docs/install/linux">Linux</a>). Під час першого запуску Sokuji відкриває майстер налаштування з шести кроків.',
      tip: 'На Windows під час запуску Sokuji пропонує встановити VB-CABLE — безкоштовний віртуальний аудіодрайвер. Натисніть Install Now, якщо хочете, щоб співрозмовник під час дзвінка чув ваш перекладений голос.',
    },
    {
      title: 'Виберіть мову інтерфейсу',
      content:
        'Це лише мова меню та кнопок Sokuji. Мови для перекладу ви оберете пізніше. Натисніть <strong>Далі</strong>.',
      screenshot: '01-language.png',
    },
    {
      title: 'Скажіть Sokuji, що ви хочете зробити',
      content:
        'Виберіть картку, яка найкраще відповідає тому, як ви користуватиметеся Sokuji. Вона визначає режим перекладу (<strong>Я</strong>, <strong>Співрозмовник</strong> або <strong>Обидва</strong>) і те, чи переклад озвучується, чи показується лише субтитрами. <strong>Розуміти, що кажуть інші</strong> дає вам живий переклад того, що ви чуєте; <strong>Бути зрозумілим на зустрічі</strong> дає змогу співрозмовнику чути ваш перекладений голос; <strong>Двостороння онлайн-розмова</strong> робить і те, і те одночасно, а щойно Sokuji почне слухати ваш застосунок для зустрічей, він також зачитуватиме вам уголос переклад співрозмовника. Дві картки <strong>Розмова віч-на-віч</strong> призначені для двох людей за одним комп’ютером. Усе це можна змінити пізніше.',
      screenshot: '02-scenario.png',
      tip: 'Для відеодзвінка з людиною, яка говорить іншою мовою, виберіть «Двостороння онлайн-розмова», а потім дотримуйтеся посібника з двосторонньої зустрічі. Для людини, яка сидить поруч із вами, виберіть «Розмова віч-на-віч» і дотримуйтеся посібника з розмови віч-на-віч.',
    },
    {
      title: 'Виберіть «Почати одразу»',
      content:
        'Виберіть <strong>Почати одразу</strong>. Тоді переклад для вас виконує Kizuna AI, тож створювати чи вставляти API-ключ не потрібно; налаштування вибирає рушій Soniox, а пізніше можна перемкнутися на Qwen (див. нижче). Натисніть <strong>Далі</strong>.',
      screenshot: '03-provider.png',
    },
    {
      title: 'Увійдіть або створіть обліковий запис',
      content:
        'Натисніть <strong>Створити акаунт</strong>, якщо ви вперше користуєтеся Kizuna AI, або <strong>Увійти</strong>, якщо обліковий запис у вас уже є.',
      screenshot: '04-account.png',
    },
    {
      title: 'Заповніть форму реєстрації',
      content:
        'Введіть ім’я, адресу електронної пошти та пароль щонайменше з 8 символів, потім натисніть <strong>Реєстрація</strong>. З тим самим обліковим записом ви входите на сайт Kizuna AI, де керуєте балансом.',
      screenshot: '05-create-account.png',
    },
    {
      title: 'Підтвердьте адресу електронної пошти',
      content:
        'Sokuji одразу виконує вхід і надсилає вам лист із посиланням для підтвердження. Відкрийте це посилання: пробний баланс буде нараховано, щойно адресу підтверджено. Потім натисніть <strong>Далі</strong>.',
      screenshot: '06-verify-email.png',
      tip: 'Листа немає? Перевірте папку «Спам». Тим часом можна завершити налаштування: баланс з’явиться на кнопці облікового запису, щойно ви підтвердите адресу.',
    },
    {
      title: 'Виберіть мови',
      content:
        '<strong>Я говорю</strong> — ваша мова; <strong>вони чують</strong> — мова, яку отримує інша сторона. У двосторонньому режимі зворотний напрямок задається автоматично, і рядок під полями показує його: тут «Вони говорять Японська → я читаю Англійська». Коли переклад співрозмовника зачитуватиметься вам уголос, там натомість написано «я чую».',
      screenshot: '07-languages.png',
      tip: 'Якщо мовлення співрозмовника теж перекладається (режим «Співрозмовник» або «Обидва»), укажіть у полі «Я говорю» конкретну мову, а не «Автовиявлення». Інакше Sokuji не знає, якою мовою перекладати співрозмовника, і кнопка «Почати» залишається недоступною.',
    },
    {
      title: 'Перевірте й завершіть',
      content:
        'Перегляньте підсумок і натисніть <strong>Завершити</strong>. Після цього почнеться короткий огляд головного вікна. Його можна пропустити й будь-коли запустити знову в розділі <strong>Налаштування → Допомога</strong>.',
      screenshot: '08-ready.png',
    },
    {
      title: 'Виберіть мікрофон і почніть',
      content:
        'Панель налаштувань праворуч показує, що було вибрано під час налаштування. У розділі <strong>Мікрофон</strong> виберіть мікрофон, у який ви говорите. Потім натисніть <strong>Почати</strong> внизу вікна. Якщо кнопка «Почати» залишається сірою, наведіть на неї курсор: підказка повідомить, чого ще бракує.',
      screenshot: '09-main-window.png',
      tip: 'У режимі «Обидва» також потрібно вказати «Аудіо співрозмовника», щоб Sokuji знав, з якого застосунку говорить інша сторона. Про це йдеться в посібнику з двосторонньої зустрічі. Для розмови віч-на-віч це не потрібно: співрозмовник користується вашим мікрофоном.',
    },
    {
      title: 'Перевірте баланс і поповніть його',
      content:
        'Кнопка облікового запису в рядку заголовка показує ваш баланс. Натисніть її, щоб побачити свою адресу електронної пошти, баланс і витрати за останні 30 днів. <strong>Поповнити</strong> відкриває в браузері сторінку оплати вашого облікового запису Kizuna AI: там ви вносите кошти й перед оплатою бачите поточні тарифи.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'Перемкніть рушій (за бажанням)',
      content:
        'KizunaAI працює на одному з двох рушіїв, і список постачальників послуг ШІ показує обидва: <strong>KizunaAI — Працює на Soniox</strong>, рекомендований рушій за замовчуванням, який вибрало налаштування, і <strong>KizunaAI — Працює на Qwen</strong>. Щоб змінити, відкрийте <strong>Налаштування</strong>, перейдіть у режим <strong>Розширений</strong> і виберіть інший у розділі <strong>Постачальник</strong>; вибір діє з наступного сеансу. Чим відрізняються обидва рушії, пояснено в розділі запитань нижче.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: 'Поради для найкращих результатів',
    items: [
      'Користуйтеся навушниками. Без них мікрофон може вловити перекладений голос і перекласти його ще раз.',
      'Якщо вам потрібні лише субтитри, вимкніть «Переклад, який чує співрозмовник» у блоці «Мовлення» налаштувань і залиште «Переклад, який чую я» вимкненим. Синтез мовлення коштує додатково.',
      'Зупиняйте сеанс, коли закінчите: запущений сеанс і далі витрачає баланс.',
      'Червона крапка на кнопці облікового запису означає, що балансу недостатньо, щоб почати сеанс; жовта — що адресу електронної пошти ще не підтверджено.',
    ],
  },
  faq: {
    title: 'Часті запитання',
    items: [
      {
        question: 'Скільки це коштує?',
        answer:
          'Переклад оплачується з передплаченого балансу за фактичне використання, без підписки. Тариф залежить від рушія та від того, що робить сеанс (наприклад, озвучення коштує дорожче, ніж лише субтитри). На сторінці оплати вашого облікового запису Kizuna AI вказано поточні тарифи — ще до поповнення.',
      },
      {
        question: 'Що станеться, коли баланс закінчиться?',
        answer:
          'Sokuji не починає сеанс, поки балансу недостатньо, а запущений сеанс зупиняється, коли його баланс вичерпано. Поповніть баланс і знову натисніть «Почати».',
      },
      {
        question: 'Куди передається мій звук?',
        answer:
          'Sokuji надсилає ваш звук безпосередньо до рушія, який ви вибрали, — Soniox або сервісу Qwen від Alibaba Cloud у вибраному вами регіоні, — використовуючи короткостроковий ключ, виданий для вашого облікового запису. Kizuna AI відповідає за ваш обліковий запис і оплату. Якщо ввімкнено контекст екрана (рушій Qwen), зображення вікна, екрана чи вкладки зустрічі, що використовується, йдуть тим самим шляхом; Kizuna AI ніколи їх не отримує.',
      },
      {
        question: 'Soniox чи Qwen: який рушій вибрати?',
        answer:
          'Soniox — універсальний варіант: 60 мов, близько 200 готових голосів або голос, клонований із запису, спільний сеанс для режиму «Обидва», розмова віч-на-віч і регіони в США, Європейському Союзі та Японії. Qwen від Alibaba Cloud перекладає мовлення в мовлення однією моделлю, озвучує 29 зі своїх 60 мов, може клонувати ваш голос наживо під час розмови без жодного запису й працює в Сінгапурі або Пекіні. Обидва оплачуються з одного балансу, кожен за своїм тарифом.',
      },
      {
        question: 'Чи може рушій Qwen бачити мої слайди?',
        answer:
          'Так: увімкніть «Контекст екрана» в налаштуваннях рушія Qwen; у настільному застосунку вікно чи екран вибирають під час ввімкнення, а розширення для браузера використовує вкладку зустрічі. Поки він увімкнений, плата за кожну секунду мовлення збільшується; докладніше — у посібнику з налаштування Qwen.',
      },
      {
        question: 'Як знову запустити майстер налаштування?',
        answer: 'Відкрийте «Налаштування», прокрутіть до розділу «Допомога» й натисніть «Запустити налаштування знову».',
      },
    ],
  },
  troubleshooting: {
    title: 'Усунення несправностей',
    content:
      'Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Також можна запитати в <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const it: TutorialData = {
  pageTitle: 'Guida rapida a KizunaAI',
  backLink: 'Torna ai fornitori di IA',
  backLinkUrl: '/docs/ai-providers',
  overview: {
    title: 'Panoramica',
    content:
      'KizunaAI è il modo più rapido per iniziare con Sokuji: non c’è nessuna chiave API da creare. Accedi con un account Kizuna AI, e la traduzione gira su uno dei due motori, Soniox o Qwen, che Kizuna AI paga e addebita sul tuo saldo. Gli account con un indirizzo email verificato ricevono un credito di prova (attualmente 0,50 US$, valido 7 giorni); dopodiché ricarichi il saldo e paghi solo quello che usi, senza abbonamento. Questa guida illustra il primo avvio dell’app desktop di Sokuji. Le schermate provengono da Sokuji 0.44.0 su Windows; su macOS e Linux l’aspetto è lo stesso.',
  },
  steps: [
    {
      title: 'Installa Sokuji',
      content:
        'Scarica l’app desktop dalla <a href="/download">pagina di download</a> e installala (vedi le guide per <a href="/docs/install/windows">Windows</a>, <a href="/docs/install/macos">macOS</a> e <a href="/docs/install/linux">Linux</a>). Al primo avvio, Sokuji apre una configurazione guidata in sei passaggi.',
      tip: 'Su Windows, all’avvio Sokuji propone di installare VB-CABLE, un driver audio virtuale gratuito. Fai clic su Install Now se vuoi che l’altra persona in chiamata senta la tua voce tradotta.',
    },
    {
      title: 'Scegli la lingua dell’interfaccia',
      content:
        'È solo la lingua dei menu e dei pulsanti di Sokuji. Le lingue tra cui tradurre le scegli più avanti. Fai clic su <strong>Avanti</strong>.',
      screenshot: '01-language.png',
    },
    {
      title: 'Di’ a Sokuji cosa vuoi fare',
      content:
        'Scegli la scheda più vicina a come userai Sokuji. Imposta la modalità di traduzione (<strong>Io</strong>, <strong>Altro</strong> o <strong>Entrambi</strong>) e stabilisce se le traduzioni vengono pronunciate o mostrate solo come sottotitoli. <strong>Capire cosa dicono gli altri</strong> ti dà una traduzione in tempo reale di ciò che senti; <strong>Farsi capire in una riunione</strong> fa sentire all’altra parte la tua voce tradotta; <strong>Conversazione online bidirezionale</strong> fa entrambe le cose insieme e, quando Sokuji ascolta la tua app per riunioni, legge anche ad alta voce per te la traduzione dell’altra parte. Le due schede <strong>Conversazione faccia a faccia</strong> sono per due persone davanti allo stesso computer. Puoi cambiare tutto questo in seguito.',
      screenshot: '02-scenario.png',
      tip: 'Per una videochiamata con qualcuno che parla un’altra lingua, scegli Conversazione online bidirezionale, poi segui la guida alle riunioni bidirezionali. Per qualcuno seduto accanto a te, scegli Conversazione faccia a faccia e segui la guida alla conversazione faccia a faccia.',
    },
    {
      title: 'Scegli «Inizia subito»',
      content:
        'Scegli <strong>Inizia subito</strong>. Kizuna AI gestisce allora la traduzione per te, quindi non c’è nessuna chiave API da creare o incollare; la configurazione sceglie il motore Soniox, e più avanti puoi passare a Qwen (vedi sotto). Fai clic su <strong>Avanti</strong>.',
      screenshot: '03-provider.png',
    },
    {
      title: 'Accedi o crea un account',
      content:
        'Fai clic su <strong>Registrati</strong> se sei nuovo su Kizuna AI, oppure su <strong>Accedi</strong> se hai già un account.',
      screenshot: '04-account.png',
    },
    {
      title: 'Compila il modulo di registrazione',
      content:
        'Inserisci il tuo nome, il tuo indirizzo email e una password di almeno 8 caratteri, poi fai clic su <strong>Registrati</strong>. Lo stesso account ti fa accedere anche al sito di Kizuna AI, dove gestisci il tuo saldo.',
      screenshot: '05-create-account.png',
    },
    {
      title: 'Verifica il tuo indirizzo email',
      content:
        'Sokuji ti fa accedere subito e ti invia via email un link di verifica. Apri quel link: il credito di prova viene aggiunto al saldo non appena l’indirizzo è verificato. Poi fai clic su <strong>Avanti</strong>.',
      screenshot: '06-verify-email.png',
      tip: 'Nessuna email? Controlla la cartella spam. Nel frattempo puoi completare la configurazione; il credito compare sul pulsante dell’account non appena verifichi.',
    },
    {
      title: 'Scegli le lingue',
      content:
        '<strong>Io parlo</strong> è la tua lingua; <strong>loro sentono</strong> è la lingua che riceve l’altra parte. In modalità bidirezionale la direzione inversa viene impostata per te, e la riga sottostante la mostra: qui, «Loro parlano Giapponese → io leggo Inglese». Dice «io sento» invece di «io leggo» quando la traduzione dell’altra parte ti verrà letta ad alta voce.',
      screenshot: '07-languages.png',
      tip: 'Se viene tradotto anche il parlato dell’altra parte (modalità Altro o Entrambi), imposta Io parlo su una lingua precisa invece di Rilevamento Automatico. Altrimenti Sokuji non sa in quale lingua tradurli, e Avvia resta disattivato.',
    },
    {
      title: 'Controlla e concludi',
      content:
        'Controlla il riepilogo e fai clic su <strong>Fine</strong>. Segue un breve tour della finestra principale. Puoi saltarlo e rivederlo in qualsiasi momento da <strong>Impostazioni → Aiuto</strong>.',
      screenshot: '08-ready.png',
    },
    {
      title: 'Scegli il microfono e avvia',
      content:
        'Il pannello Impostazioni sulla destra mostra cosa ha scelto la configurazione. In <strong>Microfono</strong>, scegli il microfono in cui parli. Poi fai clic su <strong>Avvia</strong> in fondo alla finestra. Se Avvia resta grigio, passaci sopra con il mouse: la nota ti dice cosa manca ancora.',
      screenshot: '09-main-window.png',
      tip: 'La modalità Entrambi richiede anche di impostare Audio dell\'altro, così Sokuji sa da quale app parla l’altra parte. La guida alle riunioni bidirezionali spiega come fare. Per il faccia a faccia non serve: l’altra persona condivide il tuo microfono.',
    },
    {
      title: 'Controlla il saldo e ricarica',
      content:
        'Il pulsante dell’account nella barra del titolo mostra il tuo saldo. Fai clic per vedere il tuo indirizzo email, il saldo e quanto hai speso negli ultimi 30 giorni. <strong>Ricarica</strong> apre nel browser la pagina di fatturazione del tuo account Kizuna AI, dove aggiungi fondi e puoi vedere le tariffe attuali prima di pagare.',
      screenshot: '10-balance-top-up.png',
    },
    {
      title: 'Cambia motore (facoltativo)',
      content:
        'KizunaAI gira su uno di due motori, e l’elenco dei fornitori di servizi AI li mostra entrambi: <strong>KizunaAI — Con tecnologia Soniox</strong>, quello consigliato e predefinito scelto dalla configurazione, e <strong>KizunaAI — Con tecnologia Qwen</strong>. Per cambiare, apri <strong>Impostazioni</strong>, passa ad <strong>Avanzato</strong> e scegli l’altro sotto <strong>Fornitore</strong>; la scelta vale dalla sessione successiva. Le domande frequenti qui sotto spiegano come differiscono i due.',
      screenshot: '11-engines.png',
    },
  ],
  tips: {
    title: 'Consigli per risultati migliori',
    items: [
      'Usa le cuffie. Senza, il microfono può captare la voce tradotta e tradurla di nuovo.',
      'Quando ti servono solo i sottotitoli, disattiva Traduzione che sente l\'altra parte nella sezione Voce delle Impostazioni e lascia disattivata Traduzione che sento io. Generare la voce costa di più.',
      'Interrompi la sessione quando hai finito: una sessione attiva continua a consumare il saldo.',
      'Un punto rosso sul pulsante dell’account indica che il saldo è troppo basso per avviare una sessione; un punto ambra indica che il tuo indirizzo email non è ancora verificato.',
    ],
  },
  faq: {
    title: 'Domande frequenti',
    items: [
      {
        question: 'Quanto costa?',
        answer:
          'La traduzione si paga da un saldo prepagato, in base all’uso e senza abbonamento. La tariffa dipende dal motore e da cosa fa una sessione (per esempio, l’uscita vocale costa più dei soli sottotitoli). La pagina di fatturazione del tuo account Kizuna AI mostra le tariffe attuali prima della ricarica.',
      },
      {
        question: 'Cosa succede quando il saldo si esaurisce?',
        answer:
          'Sokuji non avvia una sessione finché il saldo è troppo basso, e una sessione attiva si interrompe quando il suo saldo è esaurito. Ricarica, poi fai di nuovo clic su Avvia.',
      },
      {
        question: 'Dove va il mio audio?',
        answer:
          'Sokuji invia il tuo audio direttamente al motore che hai scelto — Soniox, oppure il servizio Qwen di Alibaba Cloud nella regione che hai scelto — usando una chiave a breve durata emessa per il tuo account. Kizuna AI gestisce il tuo account e la fatturazione. Con il contesto dello schermo attivo (motore Qwen), le immagini della finestra, dello schermo o della scheda della riunione in uso fanno la stessa strada; Kizuna AI non le riceve mai.',
      },
      {
        question: 'Soniox o Qwen: quale motore scegliere?',
        answer:
          'Soniox è il più versatile: 60 lingue, circa 200 voci predefinite o una voce clonata da una registrazione, una sessione condivisa per la modalità Entrambi, la conversazione faccia a faccia e regioni negli Stati Uniti, nell’Unione Europea e in Giappone. Qwen, di Alibaba Cloud, fa l’interpretariato da voce a voce con un unico modello, parla 29 delle sue 60 lingue, può clonare la tua voce dal vivo mentre parli senza niente da registrare, e gira a Singapore o a Pechino. Entrambi si pagano con lo stesso saldo, alle rispettive tariffe.',
      },
      {
        question: 'Il motore Qwen può vedere le mie slide?',
        answer:
          'Sì: attiva Contesto dello schermo nelle impostazioni del motore Qwen; nell’app desktop scegli la finestra o lo schermo quando lo attivi, mentre l’estensione del browser usa la scheda della riunione. Finché è attivo, aumenta l’addebito per ogni secondo di parlato; la guida alla configurazione di Qwen lo spiega.',
      },
      {
        question: 'Come riapro la configurazione guidata?',
        answer: 'Apri Impostazioni, scorri fino ad Aiuto e fai clic su Ripeti la configurazione.',
      },
    ],
  },
  troubleshooting: {
    title: 'Risoluzione dei problemi',
    content:
      'Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>. Puoi anche chiedere su <a href="https://github.com/kizuna-ai-lab/sokuji/discussions">GitHub Discussions</a>.',
  },
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function KizunaAIQuickStart() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/kizuna-ai-quick-start" />;
}
